/* eslint-disable react/forbid-dom-props */
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  Users, Scale, Calculator, ShieldCheck, Building2, FileCog, Plug,
  Plus, Trash2, Edit2, Check, ChevronRight,
  Wifi, Cpu, ToggleLeft, ToggleRight, Info, BarChart3, AlertTriangle,
  MessageSquare, ListChecks, BookOpen, Repeat, FileText,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../store/useAuthStore';
import { Modal, ConfirmModal } from '../../components/ui';
import UserAvatar from '../../components/UserAvatar';
import { customFieldsApi, jurisprudenciasApi, rolesApi, settingsApi, usersApi, metricsApi } from '../../services/api';
import { ALL_PERMISSIONS, ROLE_OPTIONS, DEFAULT_ROLE_PERMISSIONS } from '../../rbac/permissions';
import TextosPadrao from './TextosPadrao';

// ─── Metadados de cada parâmetro de cálculo ───────────────────
const PARAM_DEF = [
  // Verbas Rescisórias
  { key: 'fgts_pct', group: 'Verbas Rescisórias', label: 'FGTS mensal', suffix: '% s/ remuneração', min: 0, max: 20, step: 0.5, hint: 'Depósito obrigatório pelo empregador — CLT art. 15', description: 'Percentual aplicado sobre a remuneração mensal para estimar depósitos de FGTS.', example: 'Com salário de R$ 3.000, 8% gera R$ 240 de FGTS por mês.' },
  { key: 'multa_fgts_sem_justa_causa', group: 'Verbas Rescisórias', label: 'Multa FGTS — sem justa causa', suffix: '% s/ saldo FGTS', min: 0, max: 100, step: 1, hint: 'CLT art. 18 §1º', description: 'Percentual da multa rescisória aplicada sobre o saldo de FGTS na dispensa sem justa causa.', example: 'Saldo de FGTS de R$ 10.000 com 40% gera multa de R$ 4.000.' },
  { key: 'multa_fgts_rescisao_indireta', group: 'Verbas Rescisórias', label: 'Multa FGTS — rescisão indireta', suffix: '% s/ saldo FGTS', min: 0, max: 100, step: 1, hint: 'CLT art. 18 §1º', description: 'Percentual usado quando a rescisão indireta é reconhecida com efeitos equivalentes à dispensa sem justa causa.', example: 'Saldo de FGTS de R$ 8.000 com 40% gera multa de R$ 3.200.' },
  { key: 'aviso_previo_base_dias', group: 'Verbas Rescisórias', label: 'Aviso prévio — base', suffix: 'dias', min: 0, max: 90, step: 1, hint: 'CLT art. 487 II', description: 'Quantidade mínima de dias de aviso prévio indenizado antes dos acréscimos por tempo de serviço.', example: 'Base de 30 dias sobre salário de R$ 3.000 estima R$ 3.000 de aviso.' },
  { key: 'aviso_previo_proporcional_dias_por_ano', group: 'Verbas Rescisórias', label: 'Aviso prévio — acréscimo proporcional', suffix: 'dias/ano de serviço', min: 0, max: 10, step: 1, hint: 'Máximo de 90 dias — Lei 12.506/2011', description: 'Dias acrescentados ao aviso prévio para cada ano completo de vínculo.', example: 'Com 4 anos e acréscimo de 3 dias/ano, o aviso soma 42 dias.' },
  // Jornada
  { key: 'horas_extras_pct', group: 'Jornada', label: 'Hora extra — adicional mínimo', suffix: '% s/ hora normal', min: 50, max: 200, step: 5, hint: 'CCT pode prever percentual maior — CLT art. 59 §1º', description: 'Adicional aplicado à hora normal para calcular horas extras.', example: 'Hora normal de R$ 20 com 50% vira hora extra de R$ 30.' },
  { key: 'intervalo_intrajornada_adicional_pct', group: 'Jornada', label: 'Intervalo intrajornada — adicional', suffix: '% s/ hora normal', min: 0, max: 100, step: 5, hint: 'Incide sobre o período efetivamente suprimido — CLT art. 71 §4º', description: 'Adicional usado sobre o período de intervalo suprimido ou reduzido.', example: '1 hora suprimida com hora normal de R$ 20 e 50% resulta em R$ 30.' },
  { key: 'adicional_noturno_pct', group: 'Jornada', label: 'Adicional noturno (22h–5h)', suffix: '%', min: 0, max: 50, step: 5, hint: 'Hora noturna = 52min30s — CLT art. 73', description: 'Percentual acrescido às horas trabalhadas em período noturno urbano.', example: 'Hora de R$ 20 com 20% de adicional noturno gera R$ 24.' },
  { key: 'feriados_pct', group: 'Jornada', label: 'Feriado trabalhado — adicional', suffix: '%', min: 50, max: 200, step: 50, hint: 'Sem folga compensatória — Lei 605/49', description: 'Percentual aplicado ao trabalho em feriados quando não há compensação.', example: 'Hora de R$ 20 com 100% em feriado gera R$ 40.' },
  // Adicionais
  { key: 'adicional_insalubridade_minimo', group: 'Adicionais', label: 'Insalubridade — grau mínimo', suffix: '% s/ sal. mínimo', min: 0, max: 100, step: 5, hint: 'NR-15 — CLT art. 192', description: 'Percentual para atividades com insalubridade em grau mínimo.', example: 'Salário mínimo de R$ 1.412 com 10% gera adicional de R$ 141,20.' },
  { key: 'adicional_insalubridade_medio', group: 'Adicionais', label: 'Insalubridade — grau médio', suffix: '% s/ sal. mínimo', min: 0, max: 100, step: 5, hint: 'NR-15 — CLT art. 192', description: 'Percentual para atividades com insalubridade em grau médio.', example: 'Salário mínimo de R$ 1.412 com 20% gera adicional de R$ 282,40.' },
  { key: 'adicional_insalubridade_maximo', group: 'Adicionais', label: 'Insalubridade — grau máximo', suffix: '% s/ sal. mínimo', min: 0, max: 100, step: 5, hint: 'NR-15 — CLT art. 192', description: 'Percentual para atividades com insalubridade em grau máximo.', example: 'Salário mínimo de R$ 1.412 com 40% gera adicional de R$ 564,80.' },
  { key: 'adicional_periculosidade', group: 'Adicionais', label: 'Periculosidade', suffix: '% s/ sal. base', min: 0, max: 100, step: 5, hint: 'Incide sobre salário básico exceto eletricitários — CLT art. 193', description: 'Percentual aplicado sobre o salário-base para atividades perigosas.', example: 'Salário-base de R$ 3.000 com 30% gera adicional de R$ 900.' },
  // Multas e Encargos
  { key: 'multa_art477', group: 'Multas e Encargos', label: 'Multa art. 477 CLT', suffix: '× salário', min: 0, max: 5, step: 0.5, hint: 'Atraso no pagamento das verbas rescisórias', description: 'Multiplicador do salário usado para estimar a multa por atraso rescisório.', example: 'Salário de R$ 3.000 com 1x gera multa de R$ 3.000.' },
  { key: 'multa_art467', group: 'Multas e Encargos', label: 'Multa art. 467 CLT', suffix: '× parcelas incontroversas', min: 0, max: 2, step: 0.1, hint: 'Devida na 1ª audiência', description: 'Multiplicador aplicado às parcelas incontroversas não pagas em audiência.', example: 'R$ 5.000 incontroversos com 0,5x gera multa de R$ 2.500.' },
  { key: 'inss_empregado_pct', group: 'Multas e Encargos', label: 'INSS empregado (estimativa)', suffix: '%', min: 0, max: 20, step: 0.5, hint: 'Alíquota média — tabela progressiva vigente', description: 'Percentual médio usado para estimar desconto previdenciário do empregado.', example: 'Base de R$ 3.000 com 9% estima R$ 270 de INSS.' },
  { key: 'irrf_estimado_pct', group: 'Multas e Encargos', label: 'IRRF retido na fonte (estimativa)', suffix: '%', min: 0, max: 30, step: 0.5, hint: 'Rendimentos tributáveis acumulados (RRA)', description: 'Percentual médio usado para estimar retenção de imposto de renda.', example: 'Base tributável de R$ 10.000 com 7,5% estima R$ 750 de IRRF.' },
  // Honorários
  { key: 'honorarios_min_pct', group: 'Honorários', label: 'Contratuais — mínimo do escritório', suffix: '%', min: 5, max: 50, step: 1, hint: 'EAOAB art. 22', description: 'Percentual mínimo de referência para honorários contratuais do escritório.', example: 'Êxito de R$ 40.000 com 20% estima honorários de R$ 8.000.' },
  { key: 'honorarios_max_pct', group: 'Honorários', label: 'Contratuais — máximo do escritório', suffix: '%', min: 5, max: 50, step: 1, hint: 'EAOAB art. 22', description: 'Percentual máximo usado como limite de referência para honorários contratuais.', example: 'Êxito de R$ 40.000 com 30% estima teto de R$ 12.000.' },
  { key: 'honorarios_sucumbencia_min_pct', group: 'Honorários', label: 'Sucumbência — mínimo', suffix: '% s/ liquidação', min: 5, max: 50, step: 1, hint: 'CLT art. 791-A', description: 'Percentual mínimo para honorários de sucumbência sobre o valor liquidado.', example: 'Liquidação de R$ 30.000 com 5% gera R$ 1.500.' },
  { key: 'honorarios_sucumbencia_max_pct', group: 'Honorários', label: 'Sucumbência — máximo', suffix: '% s/ liquidação', min: 5, max: 50, step: 1, hint: 'CLT art. 791-A', description: 'Percentual máximo para honorários de sucumbência sobre o valor liquidado.', example: 'Liquidação de R$ 30.000 com 15% gera R$ 4.500.' },
];

// ─── Constantes ───────────────────────────────────────────────
const SECTIONS = [
  { id: 'usuarios',        label: 'Usuários',                icon: Users      },
  { id: 'perfis',          label: 'Perfis',                  icon: ShieldCheck },
  { id: 'escritorio',      label: 'Escritório',              icon: Building2  },
  { id: 'jurisprudencias', label: 'Jurisprudências',         icon: Scale      },
  { id: 'textos_padrao',   label: 'Textos do escritório',    icon: FileText   },
  { id: 'parametros',      label: 'Parâmetros de Cálculo',   icon: Calculator },
  { id: 'campos',          label: 'Campos da Ficha',         icon: FileCog    },
  { id: 'integracoes',     label: 'Integrações',             icon: Plug       },
  { id: 'ia',              label: 'Inteligência Artificial', icon: Cpu        },
];

const SECTION_PERMISSIONS = {
  usuarios:        ['users:manage'],
  perfis:          ['users:manage'],
  escritorio:      ['settings:office'],
  jurisprudencias: ['juris:manage', 'config:edit'],
  textos_padrao:   ['juris:manage', 'config:edit', 'settings:templates'],
  parametros:      ['settings:parametros'],
  campos:          ['settings:fields'],
  integracoes:     ['settings:integrations'],
  ia:              ['config:edit'],
};


const TIPO_PEDIDO_OPTIONS = [
  { value: 'horas_extras',    label: 'Horas extras' },
  { value: 'horas_percurso',  label: 'Horas in itinere' },
  { value: 'insalubridade',   label: 'Insalubridade' },
  { value: 'periculosidade',  label: 'Periculosidade' },
  { value: 'rescisao',        label: 'Rescisão' },
  { value: 'danos_morais',    label: 'Danos morais' },
  { value: 'outros',          label: 'Outros' },
];

// ─── Primitivos ───────────────────────────────────────────────
function SectionHeader({ title, description, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
      <div>
        <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary)' }}>{title}</h2>
        {description && <p style={{ margin: '.25rem 0 0', fontSize: '.8rem', color: 'var(--text-muted)' }}>{description}</p>}
      </div>
      {action}
    </div>
  );
}

function FG({ label, hint, children, style }) {
  return (
    <div className="form-group" style={style}>
      <label style={{ fontSize: '.78rem', fontWeight: 500 }}>{label}</label>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </div>
  );
}


function Table({ head, children }) {
  // A rolagem fica no wrapper, não na página: com `nowrap` no cabeçalho e 5 a 7 colunas, a
  // tabela mantém a largura mínima do conteúdo e empurrava o body inteiro na horizontal
  // quando o layout colapsa para uma coluna (≤900px).
  return (
    <div className="table-scroll">
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.82rem' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid var(--border-light)' }}>
            {head.map(h => (
              <th key={h} style={{ padding: '.5rem .75rem', textAlign: 'left', fontSize: '.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function TR({ children, style }) {
  return <tr style={{ borderBottom: '1px solid var(--border-light)', ...style }}>{children}</tr>;
}

function TD({ children, style }) {
  return <td style={{ padding: '.625rem .75rem', verticalAlign: 'middle', ...style }}>{children}</td>;
}

function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      style={{ background: 'none', border: 'none', cursor: disabled ? 'not-allowed' : 'pointer', padding: 0, display: 'flex', alignItems: 'center', opacity: disabled ? 0.5 : 1 }}
      title={disabled ? 'Você não pode desativar a si mesmo' : checked ? 'Desativar' : 'Ativar'}
      aria-label={disabled ? 'Você não pode desativar a si mesmo' : checked ? 'Desativar usuário' : 'Ativar usuário'}
      aria-pressed={checked}
    >
      {checked
        ? <ToggleRight size={22} color="var(--success)" />
        : <ToggleLeft size={22} color="var(--text-muted)" />}
    </button>
  );
}

function cropAvatarFile(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const size = Math.min(img.width, img.height);
      const canvas = document.createElement('canvas');
      canvas.width = 256;
      canvas.height = 256;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, (img.width - size) / 2, (img.height - size) / 2, size, size, 0, 0, 256, 256);
      canvas.toBlob(blob => {
        URL.revokeObjectURL(url);
        if (!blob) reject(new Error('Não foi possível processar a imagem.'));
        else resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }));
      }, 'image/jpeg', 0.9);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Imagem inválida.'));
    };
    img.src = url;
  });
}

function StatusDot({ status }) {
  const map = {
    connected:    { color: 'var(--success)',  label: 'Conectado'    },
    disconnected: { color: 'var(--text-muted)', label: 'Desconectado' },
    error:        { color: 'var(--error)',    label: 'Erro'         },
  };
  const s = map[status] || map.disconnected;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '.35rem', fontSize: '.75rem', color: s.color, fontWeight: 500 }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: s.color, display: 'inline-block' }} />
      {s.label}
    </span>
  );
}


// ─── SEÇÃO 2: USUÁRIOS ────────────────────────────────────────
const EMPTY_USER = { name: '', login: '', email: '', password: '', role: 'atendente', role_label: 'Atendente', permissions: DEFAULT_ROLE_PERMISSIONS.atendente, is_active: true };

function SectionUsuarios() {
  const { user: currentUser } = useAuthStore();
  const queryClient = useQueryClient();
  const [modal, setModal] = useState(null); // null | 'add' | { ...user }
  const [form, setForm] = useState(EMPTY_USER);

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users'],
    queryFn:  usersApi.list,
  });
  const { data: roles = [] } = useQuery({
    queryKey: ['roles'],
    queryFn: rolesApi.list,
  });

  const roleOptionsBase = roles.length
    ? roles.map(r => ({ value: r.name, label: r.label, permissions: r.permissions || [] }))
    : ROLE_OPTIONS.map(r => ({ ...r, permissions: DEFAULT_ROLE_PERMISSIONS[r.value] || [] }));

  // O perfil do usuário aberto pode não existir no cadastro — era o caso de "Coordenação",
  // gravado por script só em `role_label`. Sem esta linha o select exibia outro perfil qualquer
  // (o primeiro da lista) e salvar trocava o perfil da pessoa sem ninguém ter pedido.
  const perfilForaDoCadastro = !!form.role && !roleOptionsBase.some(r => r.value === form.role);
  const roleOptions = perfilForaDoCadastro
    ? [...roleOptionsBase, {
        value: form.role,
        label: `${form.role_label || form.role} — fora do cadastro`,
        permissions: form.permissions || [],
      }]
    : roleOptionsBase;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['users'] });
  const apiError = (err, fallback) => toast.error(err.response?.data?.detail || fallback);

  const createMutation = useMutation({
    mutationFn: (data) => usersApi.create(data),
    onSuccess: () => { invalidate(); toast.success('Usuário criado.'); setModal(null); },
    onError:   (err) => apiError(err, 'Erro ao criar usuário'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => usersApi.update(id, data),
    onSuccess: () => { invalidate(); toast.success('Usuário atualizado.'); setModal(null); },
    onError:   (err) => apiError(err, 'Erro ao atualizar usuário'),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }) => usersApi.update(id, { is_active: isActive }),
    onSuccess: () => { invalidate(); toast.success('Status atualizado'); },
    onError:   (err) => apiError(err, 'Erro ao atualizar status'),
  });
  const avatarMutation = useMutation({
    mutationFn: ({ id, file }) => usersApi.uploadAvatar(id, file),
    onSuccess: (updated) => {
      invalidate();
      setForm(f => f.id === updated.id ? { ...f, has_avatar: true } : f);
      if (updated.id === currentUser?.id) useAuthStore.getState().updateUser({ has_avatar: true });
      toast.success('Avatar atualizado.');
    },
    onError: (err) => apiError(err, 'Erro ao enviar avatar'),
  });

  // Trocar o perfil substituía, em silêncio, TODA a lista de permissões individuais pela do
  // perfil novo. Quem abria um usuário só para corrigir o nome e encostava no select perdia o
  // acesso ajustado à mão (foi assim que os acessos do escritório foram concedidos). Agora a
  // substituição é uma escolha visível: ligada por padrão ao criar, desligada ao editar.
  const [herdarPermissoes, setHerdarPermissoes] = useState(true);

  function openAdd() { setForm(EMPTY_USER); setHerdarPermissoes(true); setModal('add'); }
  function openEdit(u) { setForm({ ...u, password: '' }); setHerdarPermissoes(false); setModal(u); }

  function handleRoleChange(role) {
    const opt = roleOptions.find(r => r.value === role);
    setForm(f => ({
      ...f,
      role,
      role_label: opt?.label?.replace(' — fora do cadastro', '') || role,
      permissions: herdarPermissoes ? (opt?.permissions || []) : f.permissions,
    }));
  }

  function alternarHeranca(ligado) {
    setHerdarPermissoes(ligado);
    if (!ligado) return;
    const opt = roleOptions.find(r => r.value === form.role);
    if (opt) setForm(f => ({ ...f, permissions: opt.permissions || [] }));
  }

  function saveUser() {
    if (!form.name.trim() || !form.login.trim() || !form.email.trim()) { toast.error('Nome, login e e-mail são obrigatórios'); return; }
    if (modal === 'add') {
      if (!form.password || form.password.length < 6) { toast.error('Senha inicial com ao menos 6 caracteres é obrigatória'); return; }
      createMutation.mutate({
        name: form.name.trim(), login: form.login.trim(), email: form.email.trim(), password: form.password,
        role: form.role, role_label: form.role_label, permissions: form.permissions,
      });
    } else {
      const data = {
        name: form.name.trim(), login: form.login.trim(), role: form.role, role_label: form.role_label,
        permissions: form.permissions,
      };
      if (form.password) data.password = form.password;
      // Só envia o e-mail quando muda: a auditoria distingue troca de credencial de edição
      // comum, e mandar sempre marcaria toda edição como troca de credencial.
      if (form.email.trim() !== modal.email) data.email = form.email.trim();
      updateMutation.mutate({ id: form.id, data });
    }
  }

  function toggleActive(u) {
    toggleMutation.mutate({ id: u.id, isActive: !u.is_active });
  }

  function togglePerm(perm) {
    // Mexeu à mão: a lista deixa de seguir o perfil, senão a próxima troca de perfil apagaria
    // o ajuste sem aviso.
    setHerdarPermissoes(false);
    setForm(f => ({
      ...f,
      permissions: f.permissions.includes(perm) ? f.permissions.filter(p => p !== perm) : [...f.permissions, perm],
    }));
  }

  async function handleAvatarChange(e) {
    const file = e.target.files?.[0];
    if (!file || !form.id) return;
    try {
      const cropped = await cropAvatarFile(file);
      avatarMutation.mutate({ id: form.id, file: cropped });
    } catch (err) {
      toast.error(err.message || 'Imagem inválida');
    } finally {
      e.target.value = '';
    }
  }

  const isOpen = modal !== null;

  return (
    <div>
      <SectionHeader
        title="Usuários"
        description="Gerencie os membros da equipe e seus acessos"
        action={
          <button className="btn btn-primary btn-sm" onClick={openAdd}>
            <Plus size={13} /> Novo usuário
          </button>
        }
      />

      <div className="card">
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
            <div className="spinner" style={{ width: 22, height: 22 }} />
          </div>
        ) : (
        <Table head={['Nome', 'Login', 'E-mail', 'Perfil', 'Status', 'Desde', '']}>
          {users.map(u => (
            <TR key={u.id}>
              <TD>
                <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                  <UserAvatar user={u} size={28} />
                  <span style={{ fontWeight: 500 }}>{u.name}</span>
                  {u.id === currentUser?.id && <span className="badge badge-gold" style={{ fontSize: '.65rem' }}>Você</span>}
                </div>
              </TD>
              <TD style={{ color: 'var(--text-secondary)' }}>{u.login || '—'}</TD>
              <TD style={{ color: 'var(--text-secondary)' }}>{u.email}</TD>
              <TD><span className="badge badge-navy">{u.role_label}</span></TD>
              <TD><Toggle checked={u.is_active} onChange={() => toggleActive(u)} disabled={u.id === currentUser?.id} /></TD>
              <TD style={{ color: 'var(--text-muted)', fontSize: '.75rem' }}>
                {u.created_at ? new Date(u.created_at).toLocaleDateString('pt-BR') : '—'}
              </TD>
              <TD>
                <div style={{ display: 'flex', gap: '.25rem' }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => openEdit(u)} title="Editar"><Edit2 size={13} /></button>
                </div>
              </TD>
            </TR>
          ))}
        </Table>
        )}
      </div>

      <Modal isOpen={isOpen} onClose={() => setModal(null)} title={modal === 'add' ? 'Novo usuário' : 'Editar usuário'} width={520}
        footer={
          <div style={{ display: 'flex', gap: '.5rem', justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setModal(null)}>Cancelar</button>
            <button className="btn btn-primary btn-sm" onClick={saveUser} disabled={createMutation.isPending || updateMutation.isPending}>
              <Check size={13} /> {createMutation.isPending || updateMutation.isPending ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        }
      >
        <div className="form-grid fg-2" style={{ gap: '.75rem' }}>
          {modal !== 'add' && (
            <div style={{ gridColumn: '1/-1', display: 'flex', alignItems: 'center', gap: '.75rem', padding: '.625rem', border: '1px solid var(--border-light)', borderRadius: 8 }}>
              <UserAvatar user={form} size={44} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '.78rem', fontWeight: 600, color: 'var(--text-primary)' }}>Foto do usuário</div>
                <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>JPEG/PNG até 2 MB; a imagem será recortada em quadrado.</div>
              </div>
              <label className="btn btn-ghost btn-sm" style={{ cursor: 'pointer' }}>
                Enviar foto
                <input type="file" accept="image/png,image/jpeg" onChange={handleAvatarChange} style={{ display: 'none' }} />
              </label>
            </div>
          )}
          <FG label="Nome completo" style={{ gridColumn: '1/-1' }}>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Nome do usuário" />
          </FG>
          <FG label="Login">
            <input value={form.login || ''} onChange={e => setForm(f => ({ ...f, login: e.target.value }))} placeholder="erica" autoComplete="username" />
          </FG>
          <FG label="E-mail" hint={modal !== 'add' ? 'Alterar o e-mail muda como esta pessoa é identificada; a alteração fica registrada na auditoria.' : undefined}>
            <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="email@escritorio.adv.br" />
          </FG>
          <FG
            label="Perfil de acesso"
            hint={perfilForaDoCadastro
              ? 'Este perfil não está no cadastro de Perfis. Registre-o na aba Perfis para poder reaproveitá-lo.'
              : undefined}
          >
            <select value={form.role} onChange={e => handleRoleChange(e.target.value)}>
              {roleOptions.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </FG>
          <FG label={modal === 'add' ? 'Senha inicial' : 'Nova senha (opcional)'} style={{ gridColumn: '1/-1' }}>
            <input type="password" value={form.password || ''} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              placeholder={modal === 'add' ? 'Mínimo 6 caracteres' : 'Deixe em branco para manter a atual'} autoComplete="new-password" />
          </FG>
        </div>
        <div style={{ marginTop: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '.75rem', flexWrap: 'wrap', marginBottom: '.5rem' }}>
            <div style={{ fontSize: '.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-muted)' }}>
              Permissões individuais
              <span style={{ display: 'block', textTransform: 'none', letterSpacing: 0, fontWeight: 400, fontSize: '.72rem', marginTop: '.15rem' }}>
                {form.permissions.length} de {ALL_PERMISSIONS.length} — é esta lista que vale no acesso, não a do perfil.
              </span>
            </div>
            <label className="check-group" style={{ fontSize: '.75rem' }}>
              <input type="checkbox" checked={herdarPermissoes} onChange={e => alternarHeranca(e.target.checked)} />
              <span className="check-label">Usar as permissões do perfil</span>
            </label>
          </div>
          <div className="form-grid fg-2" style={{ gap: '.25rem .75rem' }}>
            {ALL_PERMISSIONS.map(p => (
              <label key={p.key} className="check-group" style={{ fontSize: '.78rem' }}>
                <input type="checkbox" checked={form.permissions.includes(p.key)} onChange={() => togglePerm(p.key)} />
                <span className="check-label">{p.label}</span>
              </label>
            ))}
          </div>
        </div>
      </Modal>

    </div>
  );
}

// ─── SEÇÃO 3: PERFIS ──────────────────────────────────────────
const EMPTY_ROLE = { name: '', label: '', permissions: [] };

function permissionGroup(label, keys) {
  return { label, permissions: ALL_PERMISSIONS.filter(p => keys.includes(p.key)) };
}

const PERMISSION_GROUPS = [
  permissionGroup('Ficha', ['ficha:create', 'ficha:edit', 'ficha:view', 'ficha:delete', 'ficha:complete_after_approval']),
  permissionGroup('Petições', ['petition:generate', 'petition:review', 'petition:approve']),
  permissionGroup('Operação', ['kanban:view', 'dashboard:view', 'case:archive']),
  permissionGroup('Configurações', ['config:edit', 'users:manage', 'settings:office', 'settings:parametros', 'settings:fields', 'settings:templates', 'settings:integrations', 'juris:manage']),
];

function SectionPerfis() {
  const queryClient = useQueryClient();
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_ROLE);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmApply, setConfirmApply] = useState(null);

  const { data: roles = [], isLoading } = useQuery({
    queryKey: ['roles'],
    queryFn: rolesApi.list,
  });
  // Crachás que aparecem na lista de Usuários e não têm linha aqui. Era o caso de "Coordenação":
  // o escritório via o perfil na tela de Usuários e não o encontrava neste cadastro.
  const { data: emUso = [] } = useQuery({
    queryKey: ['roles-em-uso'],
    queryFn: rolesApi.listEmUso,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['roles'] });
    queryClient.invalidateQueries({ queryKey: ['roles-em-uso'] });
    queryClient.invalidateQueries({ queryKey: ['users'] });
  };
  const apiError = (err, fallback) => toast.error(err.response?.data?.detail || fallback);

  const createMut = useMutation({
    mutationFn: rolesApi.create,
    onSuccess: () => { invalidate(); toast.success('Perfil criado.'); setModal(null); },
    onError: (err) => apiError(err, 'Erro ao criar perfil'),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, data }) => rolesApi.update(id, data),
    onSuccess: () => { invalidate(); toast.success('Perfil atualizado.'); setModal(null); },
    onError: (err) => apiError(err, 'Erro ao atualizar perfil'),
  });
  const deleteMut = useMutation({
    mutationFn: rolesApi.remove,
    onSuccess: () => { invalidate(); toast.success('Perfil excluído.'); setConfirmDelete(null); },
    onError: (err) => apiError(err, 'Erro ao excluir perfil'),
  });
  const applyMut = useMutation({
    mutationFn: rolesApi.applyToUsers,
    onSuccess: (data) => {
      invalidate();
      toast.success(`${data.updated_users} usuário(s) atualizados.`);
      setConfirmApply(null);
    },
    onError: (err) => apiError(err, 'Erro ao aplicar perfil'),
  });
  const registrarMut = useMutation({
    mutationFn: (data) => rolesApi.registrarEmUso(data),
    onSuccess: (role) => { invalidate(); toast.success(`Perfil "${role.label}" cadastrado.`); },
    onError: (err) => apiError(err, 'Erro ao cadastrar o perfil'),
  });

  function openAdd() { setForm(EMPTY_ROLE); setModal('add'); }
  function openEdit(role) { setForm({ ...role, permissions: role.permissions || [] }); setModal(role); }
  function togglePerm(perm) {
    setForm(f => ({
      ...f,
      permissions: f.permissions.includes(perm) ? f.permissions.filter(p => p !== perm) : [...f.permissions, perm],
    }));
  }
  function save() {
    if (!form.label.trim()) { toast.error('Nome exibido é obrigatório'); return; }
    if (modal === 'add') {
      if (!form.name.trim()) { toast.error('Código do perfil é obrigatório'); return; }
      createMut.mutate({ name: form.name, label: form.label, permissions: form.permissions });
    } else {
      updateMut.mutate({ id: form.id, data: { label: form.label, permissions: form.permissions } });
    }
  }

  return (
    <div>
      <SectionHeader
        title="Perfis"
        description="Matriz de permissões usada como base ao criar usuários; usuários mantêm um snapshot até você aplicar o perfil explicitamente."
        action={<button className="btn btn-primary btn-sm" onClick={openAdd}><Plus size={13} /> Novo perfil</button>}
      />

      {emUso.length > 0 && (
        <div className="card" style={{ borderLeft: '3px solid var(--warning)', marginBottom: '.875rem' }}>
          <div className="card-header">
            <AlertTriangle size={15} style={{ color: '#875708' }} />
            <span className="card-title">Perfis em uso que não estão neste cadastro</span>
          </div>
          <p style={{ fontSize: '.78rem', color: 'var(--text-secondary)', margin: '0 0 .75rem' }}>
            Estes nomes aparecem no crachá de usuários na aba Usuários, mas nunca foram cadastrados
            como perfil. Cadastrar não muda o acesso de ninguém: o perfil nasce com as permissões
            que essas pessoas já têm em comum.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
            {emUso.map(p => (
              <div
                key={`${p.role}:${p.role_label}`}
                style={{ display: 'flex', alignItems: 'center', gap: '.75rem', flexWrap: 'wrap', padding: '.5rem .625rem', border: '1px solid var(--border-light)', borderRadius: 8, background: 'var(--bg)' }}
              >
                <span style={{ fontWeight: 600, fontSize: '.85rem' }}>{p.role_label}</span>
                <span className="badge badge-navy" style={{ fontSize: '.68rem' }}>
                  {p.usuarios} usuário{p.usuarios > 1 ? 's' : ''}
                </span>
                <span style={{ fontSize: '.75rem', color: 'var(--text-muted)', flex: 1, minWidth: 160 }}>
                  {p.nomes.join(', ')} · {p.permissoes_comuns.length} permissões em comum
                </span>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => registrarMut.mutate({ role: p.role, role_label: p.role_label })}
                  disabled={registrarMut.isPending}
                >
                  <Plus size={13} /> Cadastrar perfil
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card">
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
            <div className="spinner" style={{ width: 22, height: 22 }} />
          </div>
        ) : roles.length === 0 ? (
          <div style={{ padding: '1.5rem', color: 'var(--text-muted)', fontSize: '.82rem' }}>Nenhum perfil cadastrado.</div>
        ) : (
          <Table head={['Perfil', 'Código', 'Permissões', 'Sistema', '']}>
            {roles.map(role => (
              <TR key={role.id}>
                <TD><span style={{ fontWeight: 600 }}>{role.label}</span></TD>
                <TD><span style={{ fontFamily: 'var(--font-mono)', fontSize: '.78rem' }}>{role.name}</span></TD>
                <TD><span className="badge badge-navy">{(role.permissions || []).length} permissões</span></TD>
                <TD>{role.is_system ? <span className="badge badge-gold">Sistema</span> : <span style={{ color: 'var(--text-muted)' }}>Personalizado</span>}</TD>
                <TD>
                  <div style={{ display: 'flex', gap: '.25rem', justifyContent: 'flex-end' }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => openEdit(role)} title="Editar"><Edit2 size={13} /></button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setConfirmApply(role)} title="Aplicar aos usuários"><Check size={13} /></button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setConfirmDelete(role)} disabled={role.is_system} title={role.is_system ? 'Perfil de sistema' : 'Excluir'} style={{ color: role.is_system ? 'var(--text-muted)' : 'var(--error)' }}><Trash2 size={13} /></button>
                  </div>
                </TD>
              </TR>
            ))}
          </Table>
        )}
      </div>

      <Modal isOpen={!!modal} onClose={() => setModal(null)} title={modal === 'add' ? 'Novo perfil' : 'Editar perfil'} width={680}
        footer={
          <div style={{ display: 'flex', gap: '.5rem', justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setModal(null)}>Cancelar</button>
            <button className="btn btn-primary btn-sm" onClick={save} disabled={createMut.isPending || updateMut.isPending}>
              <Check size={13} /> {createMut.isPending || updateMut.isPending ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        }
      >
        <div className="form-grid fg-2" style={{ gap: '.75rem', marginBottom: '1rem' }}>
          <FG label="Código" hint="Usado internamente; não muda depois de criado.">
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="financeiro" disabled={modal !== 'add'} />
          </FG>
          <FG label="Nome exibido">
            <input value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} placeholder="Financeiro" />
          </FG>
        </div>
        <div className="form-grid fg-2" style={{ gap: '.875rem' }}>
          {PERMISSION_GROUPS.map(group => (
            <div key={group.label} style={{ border: '1px solid var(--border-light)', borderRadius: 8, padding: '.75rem' }}>
              <div style={{ fontSize: '.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-muted)', marginBottom: '.5rem' }}>{group.label}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '.25rem' }}>
                {group.permissions.map(p => (
                  <label key={p.key} className="check-group" style={{ fontSize: '.78rem' }}>
                    <input type="checkbox" checked={form.permissions.includes(p.key)} onChange={() => togglePerm(p.key)} />
                    <span className="check-label">{p.label}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Modal>

      <ConfirmModal isOpen={!!confirmDelete} onClose={() => setConfirmDelete(null)}
        onConfirm={() => deleteMut.mutate(confirmDelete.id)}
        title="Excluir perfil" message="Perfis em uso ou de sistema não podem ser excluídos."
        confirmLabel="Excluir" danger />

      <ConfirmModal isOpen={!!confirmApply} onClose={() => setConfirmApply(null)}
        onConfirm={() => applyMut.mutate(confirmApply.id)}
        title="Aplicar perfil aos usuários" message="As permissões dos usuários com este perfil serão substituídas pelo snapshot atual."
        confirmLabel="Aplicar" />
    </div>
  );
}

// ─── SEÇÃO 4: ESCRITÓRIO ─────────────────────────────────────
const EMPTY_OFFICE = {
  name: '', lawyer_name: '', oab_sp: '', oab_mg: '', cnpj: '',
  address: '', phone: '', email: '', site: '',
};

function SectionEscritorio() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY_OFFICE);
  const { data, isLoading } = useQuery({
    queryKey: ['settings-office'],
    queryFn: settingsApi.getOffice,
  });

  useEffect(() => {
    if (data) setForm({ ...EMPTY_OFFICE, ...data });
  }, [data]);

  const saveMut = useMutation({
    mutationFn: settingsApi.updateOffice,
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['settings-office'] });
      const auth = useAuthStore.getState();
      if (auth.tenant) useAuthStore.setState({ tenant: { ...auth.tenant, name: updated.name } });
      toast.success('Dados do escritório salvos.');
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Erro ao salvar escritório'),
  });

  function setField(field, value) { setForm(f => ({ ...f, [field]: value })); }

  return (
    <div>
      <SectionHeader
        title="Escritório"
        description="Dados usados no timbre oficial das petições e na identificação do tenant"
        action={<button className="btn btn-primary btn-sm" onClick={() => saveMut.mutate(form)} disabled={saveMut.isPending}><Check size={13} /> {saveMut.isPending ? 'Salvando...' : 'Salvar'}</button>}
      />
      <div className="card">
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><div className="spinner" style={{ width: 22, height: 22 }} /></div>
        ) : (
          <div className="form-grid fg-2" style={{ gap: '.875rem' }}>
            <SepConfigLabel>Identidade no timbre</SepConfigLabel>
            <FG label="Nome no cabeçalho" style={{ gridColumn: '1/-1' }}><input value={form.name} onChange={e => setField('name', e.target.value)} /></FG>
            <FG label="Advogada responsável"><input value={form.lawyer_name} onChange={e => setField('lawyer_name', e.target.value)} /></FG>
            <FG label="CNPJ"><input value={form.cnpj} onChange={e => setField('cnpj', e.target.value)} /></FG>
            <FG label="OAB/SP"><input value={form.oab_sp} onChange={e => setField('oab_sp', e.target.value)} /></FG>
            <FG label="OAB/MG"><input value={form.oab_mg} onChange={e => setField('oab_mg', e.target.value)} /></FG>
            <SepConfigLabel>Contato</SepConfigLabel>
            <FG label="Endereço" style={{ gridColumn: '1/-1' }}><input value={form.address} onChange={e => setField('address', e.target.value)} /></FG>
            <FG label="Telefone"><input value={form.phone} onChange={e => setField('phone', e.target.value)} /></FG>
            <FG label="E-mail"><input value={form.email} onChange={e => setField('email', e.target.value)} /></FG>
            <FG label="Site"><input value={form.site} onChange={e => setField('site', e.target.value)} /></FG>
          </div>
        )}
      </div>
    </div>
  );
}

function SepConfigLabel({ children }) {
  return (
    <div style={{ gridColumn: '1/-1', fontSize: '.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', borderBottom: '1px solid var(--border-light)', paddingBottom: '.35rem' }}>
      {children}
    </div>
  );
}

// ─── SEÇÃO 4: JURISPRUDÊNCIAS ─────────────────────────────────
const EMPTY_JURIS = { tribunal: 'TST', numero: '', tema: '', texto_resumido: '', tipo_pedido: 'horas_extras', is_active: true, parametros: [] };

// Grupos do PARAM_DEF para exibição no checklist
const PARAM_GROUPS = [...new Set(PARAM_DEF.map(p => p.group))];

function SectionJurisprudencias() {
  const queryClient = useQueryClient();
  const { data: list = [], isLoading } = useQuery({
    queryKey: ['jurisprudencias', 'all'],
    queryFn: () => jurisprudenciasApi.list(true),
  });
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_JURIS);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [filterTipo, setFilterTipo] = useState('');

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['jurisprudencias'] });
  const apiError = (err) => toast.error(err?.response?.data?.detail || 'Erro ao salvar jurisprudência');

  const createMut = useMutation({
    mutationFn: (data) => jurisprudenciasApi.create(data),
    onSuccess: () => { invalidate(); toast.success('Jurisprudência adicionada.'); setModal(null); },
    onError: apiError,
  });
  const updateMut = useMutation({
    mutationFn: ({ id, data }) => jurisprudenciasApi.update(id, data),
    onSuccess: () => invalidate(),
    onError: apiError,
  });
  const deleteMut = useMutation({
    mutationFn: (id) => jurisprudenciasApi.remove(id),
    onSuccess: () => { invalidate(); toast.success('Jurisprudência removida.'); setConfirmDelete(null); },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Erro ao remover'),
  });

  function openAdd() { setForm(EMPTY_JURIS); setModal('add'); }
  function openEdit(j) { setForm({ ...j, parametros: j.parametros || [] }); setModal(j); }

  function toggleParamLink(key) {
    setForm(f => ({
      ...f,
      parametros: f.parametros?.includes(key)
        ? f.parametros.filter(k => k !== key)
        : [...(f.parametros || []), key],
    }));
  }

  function save() {
    if (!form.numero.trim() || !form.tema.trim()) { toast.error('Número e tema são obrigatórios'); return; }
    const data = {
      tribunal: form.tribunal, numero: form.numero, tema: form.tema,
      texto_resumido: form.texto_resumido, tipo_pedido: form.tipo_pedido,
      is_active: form.is_active, parametros: form.parametros || [],
    };
    if (modal === 'add') {
      createMut.mutate(data);
    } else {
      updateMut.mutate({ id: form.id, data }, {
        onSuccess: () => { toast.success('Jurisprudência atualizada.'); setModal(null); },
      });
    }
  }

  function remove(id) { deleteMut.mutate(id); }
  function toggleActive(j) { updateMut.mutate({ id: j.id, data: { is_active: !j.is_active } }); }

  const filtered = filterTipo ? list.filter(j => j.tipo_pedido === filterTipo) : list;

  return (
    <div>
      <SectionHeader
        title="Banco de Jurisprudências"
        description="Súmulas e OJs vinculadas aos parâmetros de cálculo e disponíveis para seleção na ficha"
        action={
          <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center' }}>
            <select value={filterTipo} onChange={e => setFilterTipo(e.target.value)} style={{ fontSize: '.78rem', padding: '.3rem .5rem' }}>
              <option value="">Todos os tipos</option>
              {TIPO_PEDIDO_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <button className="btn btn-primary btn-sm" onClick={openAdd}><Plus size={13} /> Adicionar</button>
          </div>
        }
      />

      <div className="card">
        {isLoading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
            <div className="spinner" style={{ width: 22, height: 22 }} />
          </div>
        )}
        {!isLoading && (
        <Table head={['Tribunal', 'Número / Tema', 'Tipo de Pedido', 'Parâmetros vinculados', 'Ativo', '']}>
          {filtered.map(j => {
            const linkedParams = PARAM_DEF.filter(p => (j.parametros || []).includes(p.key));
            return (
              <TR key={j.id} style={{ opacity: j.is_active ? 1 : 0.5 }}>
                <TD><span className="badge badge-navy" style={{ fontSize: '.7rem' }}>{j.tribunal}</span></TD>
                <TD>
                  <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, fontSize: '.8rem', whiteSpace: 'nowrap' }}>{j.numero}</div>
                  <div style={{ fontWeight: 500, marginTop: '.125rem' }}>{j.tema}</div>
                  <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: '.125rem', maxWidth: 300 }}>{j.texto_resumido}</div>
                </TD>
                <TD><span className="badge badge-gold" style={{ fontSize: '.68rem' }}>{TIPO_PEDIDO_OPTIONS.find(o => o.value === j.tipo_pedido)?.label || j.tipo_pedido}</span></TD>
                <TD>
                  {linkedParams.length === 0
                    ? <span style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>—</span>
                    : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.25rem' }}>
                        {linkedParams.map(p => (
                          <span key={p.key} style={{ fontSize: '.65rem', padding: '.1rem .35rem', borderRadius: 4, background: 'rgba(26,35,50,.07)', color: 'var(--text-primary)', border: '1px solid var(--border-light)', fontWeight: 500 }}>
                            {p.label}
                          </span>
                        ))}
                      </div>
                    )
                  }
                </TD>
                <TD><Toggle checked={j.is_active} onChange={() => toggleActive(j)} /></TD>
                <TD>
                  <div style={{ display: 'flex', gap: '.25rem' }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => openEdit(j)}><Edit2 size={13} /></button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setConfirmDelete(j.id)} style={{ color: 'var(--error)' }}><Trash2 size={13} /></button>
                  </div>
                </TD>
              </TR>
            );
          })}
        </Table>
        )}
      </div>

      <Modal isOpen={!!modal} onClose={() => setModal(null)} title={modal === 'add' ? 'Nova jurisprudência' : 'Editar jurisprudência'} width={620}
        footer={
          <div style={{ display: 'flex', gap: '.5rem', justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setModal(null)}>Cancelar</button>
            <button className="btn btn-primary btn-sm" onClick={save} disabled={createMut.isPending || updateMut.isPending}>
              <Check size={13} /> {createMut.isPending || updateMut.isPending ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '.75rem' }}>
          <div className="form-grid fg-2" style={{ gap: '.75rem' }}>
            <FG label="Tribunal">
              <select value={form.tribunal} onChange={e => setForm(f => ({ ...f, tribunal: e.target.value }))}>
                {['TST','STF','STJ','TRT2','TRT3','TRT15'].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </FG>
            <FG label="Número *" hint="Ex: Súmula 338, OJ SDI-I 394">
              <input value={form.numero} onChange={e => setForm(f => ({ ...f, numero: e.target.value }))} placeholder="Súmula 000" />
            </FG>
            <FG label="Tema *" style={{ gridColumn: '1/-1' }}>
              <input value={form.tema} onChange={e => setForm(f => ({ ...f, tema: e.target.value }))} placeholder="Descrição curta do tema" />
            </FG>
            <FG label="Tipo de pedido relacionado">
              <select value={form.tipo_pedido} onChange={e => setForm(f => ({ ...f, tipo_pedido: e.target.value }))}>
                {TIPO_PEDIDO_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </FG>
            <FG label="Ativo">
              <div style={{ paddingTop: '.375rem' }}>
                <Toggle checked={form.is_active} onChange={v => setForm(f => ({ ...f, is_active: v }))} />
              </div>
            </FG>
            <FG label="Texto resumido" style={{ gridColumn: '1/-1' }}>
              <textarea value={form.texto_resumido} onChange={e => setForm(f => ({ ...f, texto_resumido: e.target.value }))}
                rows={3} placeholder="Ementa ou resumo da súmula/OJ..." style={{ resize: 'vertical' }} />
            </FG>
          </div>

          {/* Parâmetros de cálculo vinculados */}
          <div>
            <div style={{ fontSize: '.78rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '.5rem', display: 'flex', alignItems: 'center', gap: '.375rem' }}>
              <Calculator size={13} style={{ color: 'var(--gold)' }} />
              Parâmetros de cálculo vinculados
            </div>
            <div style={{ fontSize: '.73rem', color: 'var(--text-muted)', marginBottom: '.625rem' }}>
              Esta súmula/OJ fundamenta quais percentuais ou regras de cálculo?
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '.625rem' }}>
              {PARAM_GROUPS.map(group => (
                <div key={group}>
                  <div style={{ fontSize: '.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', marginBottom: '.3rem' }}>{group}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '.2rem' }}>
                    {PARAM_DEF.filter(p => p.group === group).map(p => {
                      const linked = (form.parametros || []).includes(p.key);
                      return (
                        <label key={p.key} style={{ display: 'flex', alignItems: 'center', gap: '.5rem', cursor: 'pointer', padding: '.25rem .375rem', borderRadius: 4, background: linked ? 'rgba(200,144,42,.07)' : 'transparent', border: `1px solid ${linked ? 'rgba(200,144,42,.3)' : 'transparent'}` }}>
                          <input type="checkbox" checked={linked} onChange={() => toggleParamLink(p.key)} style={{ flexShrink: 0 }} />
                          <span style={{ fontSize: '.78rem', color: 'var(--text-primary)', fontWeight: linked ? 600 : 400 }}>{p.label}</span>
                          <span style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginLeft: 'auto' }}>{p.suffix}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Modal>

      <ConfirmModal isOpen={!!confirmDelete} onClose={() => setConfirmDelete(null)}
        onConfirm={() => remove(confirmDelete)}
        title="Remover jurisprudência" message="A súmula/OJ será removida do banco e não poderá ser selecionada em novas fichas."
        confirmLabel="Remover" danger />
    </div>
  );
}

// ─── SEÇÃO 6: PARÂMETROS DE CÁLCULO ──────────────────────────

// Chips de jurisprudências vinculadas — carregadas do corpus real (cache do react-query)
function LinkedJurisChips({ fieldKey }) {
  const { data: list = [] } = useQuery({
    queryKey: ['jurisprudencias', 'all'],
    queryFn: () => jurisprudenciasApi.list(true),
  });
  const linked = list.filter(j => j.is_active && (j.parametros || []).includes(fieldKey));
  if (linked.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.25rem', marginTop: '.375rem' }}>
      {linked.map(j => (
        <span
          key={j.id}
          title={j.texto_resumido}
          style={{
            fontSize: '.62rem', padding: '.1rem .35rem', borderRadius: 4,
            background: 'rgba(200,144,42,.1)', color: 'var(--gold)',
            border: '1px solid rgba(200,144,42,.3)',
            fontWeight: 600, cursor: 'help', fontFamily: 'var(--font-mono)',
            display: 'inline-flex', alignItems: 'center', gap: '.2rem', userSelect: 'none',
          }}
        >
          <Scale size={8} /> {j.tribunal} — {j.numero}
        </span>
      ))}
    </div>
  );
}

function SectionParametros() {
  const { tenant } = useAuthStore();
  const [values, setValues] = useState(tenant?.settings || {});

  useEffect(() => {
    setValues(tenant?.settings || {});
  }, [tenant]);

  const saveMut = useMutation({
    mutationFn: settingsApi.updateParametros,
    onSuccess: (data) => {
      const auth = useAuthStore.getState();
      if (auth.tenant) useAuthStore.setState({ tenant: { ...auth.tenant, settings: data.settings } });
      toast.success('Parâmetros salvos.');
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Erro ao salvar parâmetros'),
  });

  // NumParam lê metadados do PARAM_DEF e busca jurisprudências vinculadas do contexto
  function NumParam({ fieldKey }) {
    const def = PARAM_DEF.find(d => d.key === fieldKey);
    if (!def) return null;
    return (
      <div className="form-group">
        <label style={{ fontSize: '.78rem', fontWeight: 500 }}>{def.label}</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
          <input
            type="number"
            value={values[fieldKey] ?? 0}
            min={def.min}
            max={def.max}
            step={def.step}
            onChange={e => setValues(v => ({ ...v, [fieldKey]: e.target.value === '' ? '' : Number(e.target.value) }))}
            style={{ maxWidth: 88, fontFamily: 'var(--font-mono)' }}
          />
          {def.suffix && <span style={{ fontSize: '.82rem', color: 'var(--text-muted)', flexShrink: 0 }}>{def.suffix}</span>}
        </div>
        {def.hint && <span className="field-hint">{def.hint}</span>}
        {(def.description || def.example) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.25rem', marginTop: '.25rem', fontSize: '.72rem', color: 'var(--text-muted)', lineHeight: 1.45 }}>
            {def.description && <span>{def.description}</span>}
            {def.example && <span><strong style={{ color: 'var(--text-secondary)' }}>Exemplo:</strong> {def.example}</span>}
          </div>
        )}
        <LinkedJurisChips fieldKey={fieldKey} />
      </div>
    );
  }

  const cardBody = { padding: '.5rem 1rem 1rem', display: 'flex', flexDirection: 'column', gap: '.875rem' };

  return (
    <div>
      <SectionHeader
        title="Parâmetros de Cálculo"
        description="Percentuais e coeficientes vigentes do escritório. Eles alimentam a redação assistida por IA e o motor de cálculo determinístico; os chips dourados mostram as súmulas e OJs vinculadas."
        action={<button className="btn btn-primary btn-sm" onClick={() => saveMut.mutate(values)} disabled={saveMut.isPending}><Check size={13} /> {saveMut.isPending ? 'Salvando...' : 'Salvar'}</button>}
      />

      {/* Aviso ADC 58 */}
      <div style={{ display: 'flex', gap: '.625rem', padding: '.625rem .875rem', background: 'rgba(200,144,42,.06)', border: '1px solid rgba(200,144,42,.25)', borderRadius: 8, marginBottom: '1.25rem' }}>
        <Info size={14} style={{ color: 'var(--gold)', flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: '.78rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
          <strong>Correção monetária (ADC 58/STF):</strong> fase pré-judicial = IPCA-E + TR; fase judicial = SELIC exclusivamente. Esses índices são fixados pelo STF e não são editáveis aqui.
          <LinkedJurisChips fieldKey="_adc58" />
        </div>
      </div>

      <div className="form-grid fg-3">

        <div className="card">
          <div className="card-header"><Scale size={14} color="var(--gold)" /><span className="card-title">Verbas Rescisórias</span></div>
          <div style={cardBody}>
            <NumParam fieldKey="fgts_pct" />
            <NumParam fieldKey="multa_fgts_sem_justa_causa" />
            <NumParam fieldKey="multa_fgts_rescisao_indireta" />
            <NumParam fieldKey="aviso_previo_base_dias" />
            <NumParam fieldKey="aviso_previo_proporcional_dias_por_ano" />
          </div>
        </div>

        <div className="card">
          <div className="card-header"><span className="card-title">Jornada de Trabalho</span></div>
          <div style={cardBody}>
            <NumParam fieldKey="horas_extras_pct" />
            <NumParam fieldKey="intervalo_intrajornada_adicional_pct" />
            <NumParam fieldKey="adicional_noturno_pct" />
            <NumParam fieldKey="feriados_pct" />
          </div>
        </div>

        <div className="card">
          <div className="card-header"><span className="card-title">Adicionais Remuneratórios</span></div>
          <div style={cardBody}>
            <NumParam fieldKey="adicional_insalubridade_minimo" />
            <NumParam fieldKey="adicional_insalubridade_medio" />
            <NumParam fieldKey="adicional_insalubridade_maximo" />
            <NumParam fieldKey="adicional_periculosidade" />
          </div>
        </div>

        <div className="card">
          <div className="card-header"><span className="card-title">Multas e Encargos</span></div>
          <div style={cardBody}>
            <NumParam fieldKey="multa_art477" />
            <NumParam fieldKey="multa_art467" />
            <NumParam fieldKey="inss_empregado_pct" />
            <NumParam fieldKey="irrf_estimado_pct" />
          </div>
        </div>

        <div className="card">
          <div className="card-header"><Scale size={14} color="var(--gold)" /><span className="card-title">Honorários Advocatícios</span></div>
          <div style={cardBody}>
            <NumParam fieldKey="honorarios_min_pct" />
            <NumParam fieldKey="honorarios_max_pct" />
            <NumParam fieldKey="honorarios_sucumbencia_min_pct" />
            <NumParam fieldKey="honorarios_sucumbencia_max_pct" />
          </div>
        </div>

      </div>

    </div>
  );
}

// ─── SEÇÃO 7: CAMPOS, TEMPLATES E INTEGRAÇÕES ────────────────
const FIELD_TYPES = [
  { value: 'text', label: 'Texto' },
  { value: 'textarea', label: 'Texto longo' },
  { value: 'number', label: 'Número' },
  { value: 'date', label: 'Data' },
  { value: 'select', label: 'Seleção' },
  { value: 'checkbox', label: 'Sim/Não' },
];
const FORM_TABS = [
  'identificacao', 'reclamante', 'reclamado', 'contrato', 'jornada',
  'fatos', 'juridico', 'valores', 'prompt',
];
const EMPTY_FIELD = { label: '', field_type: 'text', tab: 'contrato', options: [], required: false, is_active: true };
const EMPTY_TEMPLATE = { name: '', description: '', action_type: 'Reclamação Trabalhista', prompt_hint: '', sections: [], is_default: false, is_active: true };
const WHATSAPP_CONFIG_SAMPLE = {
  base_url: 'https://api.exemplo-whatsapp.com',
  instance: 'escritorio-principal',
};
const EMPTY_INTEGRATION = {
  key: 'whatsapp_gateway',
  label: 'WhatsApp genérico',
  config: WHATSAPP_CONFIG_SAMPLE,
  secret: '',
  is_active: false,
};
const arrayToLines = (arr) => (arr || []).join('\n');
const linesToArray = (text) => text.split('\n').map(s => s.trim()).filter(Boolean);
const isWhatsappIntegrationKey = (key = '') => key.startsWith('whatsapp_');

function SectionCampos() {
  const queryClient = useQueryClient();
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_FIELD);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const { data: fields = [], isLoading } = useQuery({ queryKey: ['custom-fields', 'all'], queryFn: () => customFieldsApi.list(true) });
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['custom-fields'] });
    queryClient.invalidateQueries({ queryKey: ['custom-fields', 'active'] });
  };
  const saveMut = useMutation({
    mutationFn: ({ id, data }) => id ? customFieldsApi.update(id, data) : customFieldsApi.create(data),
    onSuccess: () => { invalidate(); toast.success('Campo salvo.'); setModal(null); },
    onError: (err) => toast.error(err.response?.data?.detail || 'Erro ao salvar campo'),
  });
  const deleteMut = useMutation({
    mutationFn: customFieldsApi.remove,
    onSuccess: () => { invalidate(); toast.success('Campo removido.'); setConfirmDelete(null); },
    onError: (err) => toast.error(err.response?.data?.detail || 'Erro ao remover campo'),
  });
  function openAdd() { setForm({ ...EMPTY_FIELD }); setModal('add'); }
  function openEdit(field) { setForm({ ...field }); setModal(field); }
  function save() {
    if (!form.label.trim()) { toast.error('Label é obrigatório'); return; }
    saveMut.mutate({ id: modal === 'add' ? null : form.id, data: form });
  }
  return (
    <div>
      <SectionHeader title="Campos da Ficha" description="Campos personalizados ativos aparecem na aba escolhida e persistem em ficha_json.custom." action={<button className="btn btn-primary btn-sm" onClick={openAdd}><Plus size={13} /> Novo campo</button>} />
      <div className="card">
        {isLoading ? <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><div className="spinner" style={{ width: 22, height: 22 }} /></div> : (
          <Table head={['Label', 'Aba', 'Tipo', 'Obrigatório', 'Ativo', '']}>
            {fields.map(f => (
              <TR key={f.id} style={{ opacity: f.is_active ? 1 : 0.55 }}>
                <TD><strong>{f.label}</strong></TD>
                <TD><span className="badge badge-navy">{f.tab}</span></TD>
                <TD>{FIELD_TYPES.find(t => t.value === f.field_type)?.label || f.field_type}</TD>
                <TD>{f.required ? 'Sim' : 'Não'}</TD>
                <TD><Toggle checked={f.is_active} onChange={(v) => saveMut.mutate({ id: f.id, data: { is_active: v } })} /></TD>
                <TD><div style={{ display: 'flex', gap: '.25rem' }}><button className="btn btn-ghost btn-sm" onClick={() => openEdit(f)}><Edit2 size={13} /></button><button className="btn btn-ghost btn-sm" onClick={() => setConfirmDelete(f)} style={{ color: 'var(--error)' }}><Trash2 size={13} /></button></div></TD>
              </TR>
            ))}
          </Table>
        )}
      </div>
      <Modal isOpen={!!modal} onClose={() => setModal(null)} title={modal === 'add' ? 'Novo campo' : 'Editar campo'} width={560}
        footer={<div style={{ display: 'flex', gap: '.5rem', justifyContent: 'flex-end' }}><button className="btn btn-ghost btn-sm" onClick={() => setModal(null)}>Cancelar</button><button className="btn btn-primary btn-sm" onClick={save} disabled={saveMut.isPending}><Check size={13} /> Salvar</button></div>}>
        <div className="form-grid fg-2" style={{ gap: '.75rem' }}>
          <FG label="Label" style={{ gridColumn: '1/-1' }}><input value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} /></FG>
          <FG label="Aba"><select value={form.tab} onChange={e => setForm(f => ({ ...f, tab: e.target.value }))}>{FORM_TABS.map(t => <option key={t} value={t}>{t}</option>)}</select></FG>
          <FG label="Tipo"><select value={form.field_type} onChange={e => setForm(f => ({ ...f, field_type: e.target.value }))}>{FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}</select></FG>
          <FG label="Opções" hint="Uma opção por linha; usado apenas em seleção." style={{ gridColumn: '1/-1' }}><textarea rows={3} value={arrayToLines(form.options)} onChange={e => setForm(f => ({ ...f, options: linesToArray(e.target.value) }))} /></FG>
          <label className="check-group"><input type="checkbox" checked={form.required} onChange={e => setForm(f => ({ ...f, required: e.target.checked }))} /><span className="check-label">Obrigatório visualmente</span></label>
          <label className="check-group"><input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} /><span className="check-label">Ativo</span></label>
        </div>
      </Modal>
      <ConfirmModal isOpen={!!confirmDelete} onClose={() => setConfirmDelete(null)} onConfirm={() => deleteMut.mutate(confirmDelete.id)} title="Remover campo" message="O campo deixará de aparecer nas fichas novas; valores já salvos permanecem no JSON da ficha." confirmLabel="Remover" danger />
    </div>
  );
}

function SectionTemplates() {
  const queryClient = useQueryClient();
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_TEMPLATE);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const { data: templates = [], isLoading } = useQuery({ queryKey: ['petition-templates'], queryFn: () => settingsApi.listTemplates(true) });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['petition-templates'] });
  const saveMut = useMutation({
    mutationFn: ({ id, data }) => id ? settingsApi.updateTemplate(id, data) : settingsApi.createTemplate(data),
    onSuccess: () => { invalidate(); toast.success('Template salvo.'); setModal(null); },
    onError: (err) => toast.error(err.response?.data?.detail || 'Erro ao salvar template'),
  });
  const deleteMut = useMutation({ mutationFn: settingsApi.removeTemplate, onSuccess: () => { invalidate(); toast.success('Template removido.'); setConfirmDelete(null); } });
  function openAdd() { setForm({ ...EMPTY_TEMPLATE }); setModal('add'); }
  function openEdit(tpl) { setForm({ ...tpl }); setModal(tpl); }
  function save() { if (!form.name.trim()) { toast.error('Nome é obrigatório'); return; } saveMut.mutate({ id: modal === 'add' ? null : form.id, data: form }); }
  return (
    <div>
      <SectionHeader title="Templates" description="Modelos de petição persistidos para seleção futura no pipeline." action={<button className="btn btn-primary btn-sm" onClick={openAdd}><Plus size={13} /> Novo template</button>} />
      <div className="card">
        {isLoading ? <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><div className="spinner" style={{ width: 22, height: 22 }} /></div> : (
          <Table head={['Nome', 'Tipo de ação', 'Seções', 'Padrão', 'Ativo', '']}>
            {templates.map(t => (
              <TR key={t.id} style={{ opacity: t.is_active ? 1 : 0.55 }}>
                <TD><strong>{t.name}</strong><div style={{ color: 'var(--text-muted)', fontSize: '.72rem' }}>{t.description}</div></TD>
                <TD>{t.action_type || '—'}</TD>
                <TD>{(t.sections || []).length}</TD>
                <TD>{t.is_default ? <span className="badge badge-gold">Padrão</span> : '—'}</TD>
                <TD><Toggle checked={t.is_active} onChange={(v) => saveMut.mutate({ id: t.id, data: { is_active: v } })} /></TD>
                <TD><div style={{ display: 'flex', gap: '.25rem' }}><button className="btn btn-ghost btn-sm" onClick={() => openEdit(t)}><Edit2 size={13} /></button><button className="btn btn-ghost btn-sm" onClick={() => setConfirmDelete(t)} style={{ color: 'var(--error)' }}><Trash2 size={13} /></button></div></TD>
              </TR>
            ))}
          </Table>
        )}
      </div>
      <Modal isOpen={!!modal} onClose={() => setModal(null)} title={modal === 'add' ? 'Novo template' : 'Editar template'} width={620}
        footer={<div style={{ display: 'flex', gap: '.5rem', justifyContent: 'flex-end' }}><button className="btn btn-ghost btn-sm" onClick={() => setModal(null)}>Cancelar</button><button className="btn btn-primary btn-sm" onClick={save}><Check size={13} /> Salvar</button></div>}>
        <div className="form-grid fg-2" style={{ gap: '.75rem' }}>
          <FG label="Nome" style={{ gridColumn: '1/-1' }}><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></FG>
          <FG label="Descrição" style={{ gridColumn: '1/-1' }}><textarea rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></FG>
          <FG label="Tipo de ação"><input value={form.action_type} onChange={e => setForm(f => ({ ...f, action_type: e.target.value }))} /></FG>
          <FG label="Seções" hint="Uma seção por linha"><textarea rows={4} value={arrayToLines(form.sections)} onChange={e => setForm(f => ({ ...f, sections: linesToArray(e.target.value) }))} /></FG>
          <FG label="Diretriz de prompt" style={{ gridColumn: '1/-1' }}><textarea rows={3} value={form.prompt_hint} onChange={e => setForm(f => ({ ...f, prompt_hint: e.target.value }))} /></FG>
          <label className="check-group"><input type="checkbox" checked={form.is_default} onChange={e => setForm(f => ({ ...f, is_default: e.target.checked }))} /><span className="check-label">Template padrão</span></label>
          <label className="check-group"><input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} /><span className="check-label">Ativo</span></label>
        </div>
      </Modal>
      <ConfirmModal isOpen={!!confirmDelete} onClose={() => setConfirmDelete(null)} onConfirm={() => deleteMut.mutate(confirmDelete.id)} title="Remover template" message="O template será removido da configuração." confirmLabel="Remover" danger />
    </div>
  );
}

function SectionIntegracoes() {
  const queryClient = useQueryClient();
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_INTEGRATION);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const { data: integrations = [], isLoading } = useQuery({ queryKey: ['integrations'], queryFn: settingsApi.listIntegrations });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['integrations'] });
  const saveMut = useMutation({
    mutationFn: ({ id, data }) => id ? settingsApi.updateIntegration(id, data) : settingsApi.createIntegration(data),
    onSuccess: () => { invalidate(); toast.success('Integração salva.'); setModal(null); },
    onError: (err) => toast.error(err.response?.data?.detail || 'Erro ao salvar integração'),
  });
  const deleteMut = useMutation({ mutationFn: settingsApi.removeIntegration, onSuccess: () => { invalidate(); toast.success('Integração removida.'); setConfirmDelete(null); } });
  function openAdd() { setForm({ ...EMPTY_INTEGRATION, config_text: JSON.stringify(EMPTY_INTEGRATION.config, null, 2) }); setModal('add'); }
  function openEdit(row) { setForm({ ...row, secret: '', config_text: JSON.stringify(row.config || {}, null, 2) }); setModal(row); }
  function save() {
    let config = {};
    try { config = JSON.parse(form.config_text || '{}'); } catch { toast.error('Config JSON inválido'); return; }
    const data = { key: form.key, label: form.label, config, is_active: form.is_active };
    if (form.secret) data.secret = form.secret;
    saveMut.mutate({ id: modal === 'add' ? null : form.id, data });
  }
  const modalIsWhatsapp = isWhatsappIntegrationKey(form.key);
  return (
    <div>
      <SectionHeader title="Integrações" description="Configurações persistidas com segredos cifrados e mascarados nas respostas. WhatsApp usa contrato genérico para WAHA, Evolution API ou UAZAPI; nenhum provedor é chamado nesta versão." action={<button className="btn btn-primary btn-sm" onClick={openAdd}><Plus size={13} /> Nova integração</button>} />
      <div className="card">
        {isLoading ? <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><div className="spinner" style={{ width: 22, height: 22 }} /></div> : (
          <Table head={['Integração', 'Chave', 'Segredo', 'Status', '']}>
            {integrations.map(i => (
              <TR key={i.id}>
                <TD>
                  <strong>{i.label}</strong>
                  {isWhatsappIntegrationKey(i.key) && (
                    <div style={{ color: 'var(--text-muted)', fontSize: '.72rem' }}>
                      base_url + instance/session + token cifrado
                    </div>
                  )}
                </TD>
                <TD><span style={{ fontFamily: 'var(--font-mono)' }}>{i.key}</span></TD>
                <TD>{i.secret_masked || '—'}</TD>
                <TD><StatusDot status={i.is_active ? 'connected' : 'disconnected'} /></TD>
                <TD><div style={{ display: 'flex', gap: '.25rem' }}><button className="btn btn-ghost btn-sm" onClick={() => openEdit(i)}><Edit2 size={13} /></button><button className="btn btn-ghost btn-sm" onClick={() => setConfirmDelete(i)} style={{ color: 'var(--error)' }}><Trash2 size={13} /></button></div></TD>
              </TR>
            ))}
          </Table>
        )}
      </div>
      <Modal isOpen={!!modal} onClose={() => setModal(null)} title={modal === 'add' ? 'Nova integração' : 'Editar integração'} width={620}
        footer={<div style={{ display: 'flex', gap: '.5rem', justifyContent: 'flex-end' }}><button className="btn btn-ghost btn-sm" onClick={() => setModal(null)}>Cancelar</button><button className="btn btn-primary btn-sm" onClick={save}><Check size={13} /> Salvar</button></div>}>
        <div className="form-grid fg-2" style={{ gap: '.75rem' }}>
          {modalIsWhatsapp && (
            <div className="alert alert-info" style={{ gridColumn: '1/-1', margin: 0 }}>
              Contrato WhatsApp: informe `base_url` e `instance` ou `session` no JSON. O token/API key fica no campo Segredo e é salvo cifrado.
            </div>
          )}
          <FG label="Chave" hint="Use prefixo whatsapp_ para integrações WhatsApp genéricas."><input value={form.key} disabled={modal !== 'add'} onChange={e => setForm(f => ({ ...f, key: e.target.value }))} /></FG>
          <FG label="Nome"><input value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} /></FG>
          <FG label={modalIsWhatsapp ? 'Token do provedor' : 'Segredo'} hint={form.secret_masked ? `Atual: ${form.secret_masked}` : modalIsWhatsapp ? 'Obrigatório para WhatsApp; nunca aparece em texto puro.' : 'Opcional'} style={{ gridColumn: '1/-1' }}><input type="password" value={form.secret || ''} onChange={e => setForm(f => ({ ...f, secret: e.target.value }))} /></FG>
          <FG label="Config JSON" hint={modalIsWhatsapp ? 'Campos obrigatórios: base_url e instance ou session. Não coloque token aqui.' : 'Objeto JSON livre da integração.'} style={{ gridColumn: '1/-1' }}><textarea rows={6} value={form.config_text || '{}'} onChange={e => setForm(f => ({ ...f, config_text: e.target.value }))} style={{ fontFamily: 'var(--font-mono)' }} /></FG>
          <label className="check-group"><input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} /><span className="check-label">Ativa</span></label>
        </div>
      </Modal>
      <ConfirmModal isOpen={!!confirmDelete} onClose={() => setConfirmDelete(null)} onConfirm={() => deleteMut.mutate(confirmDelete.id)} title="Remover integração" message="A configuração e seu segredo cifrado serão removidos." confirmLabel="Remover" danger />
    </div>
  );
}

// ─── SEÇÃO 8: IA ─────────────────────────────────────────────
function IntegrationCard({ icon: Icon, title, description, status, children }) {
  return (
    <div className="card">
      <div style={{ padding: '.875rem 1rem .5rem', display: 'flex', alignItems: 'flex-start', gap: '.75rem' }}>
        <div style={{ width: 36, height: 36, background: 'var(--bg-hover)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon size={17} color="var(--navy)" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '.5rem' }}>
            <div style={{ fontWeight: 700, fontSize: '.88rem', color: 'var(--text-primary)' }}>{title}</div>
            <StatusDot status={status} />
          </div>
          <div style={{ fontSize: '.75rem', color: 'var(--text-muted)', marginTop: '.125rem' }}>{description}</div>
        </div>
      </div>
      <div style={{ padding: '.5rem 1rem 1rem' }}>{children}</div>
    </div>
  );
}

// Catálogo de modelos: rótulo humano e preço por milhão de tokens (entrada/saída, em dólar).
// Os preços espelham `backend/app/services/llm/pricing.py` — a tela mostrava só o identificador
// técnico ("gpt-5.4-mini"), que não ajuda ninguém a escolher entre dois modelos.
const MODEL_CATALOG = {
  'claude-haiku-4-5': { label: 'Claude Haiku 4.5', porte: 'rápido e barato', preco: [1.00, 5.00] },
  'claude-sonnet-5':  { label: 'Claude Sonnet 5',  porte: 'equilibrado',     preco: [3.00, 15.00] },
  'claude-opus-4-8':  { label: 'Claude Opus 4.8',  porte: 'o mais capaz',    preco: [5.00, 25.00] },
  'claude-fable-5':   { label: 'Claude Fable 5',   porte: 'raciocínio longo', preco: [10.00, 50.00] },
  'gpt-5.4-nano':     { label: 'GPT-5.4 nano',     porte: 'rápido e barato', preco: [0.20, 1.25] },
  'gpt-5.4-mini':     { label: 'GPT-5.4 mini',     porte: 'equilibrado',     preco: [0.75, 4.50] },
  'gpt-5.4':          { label: 'GPT-5.4',          porte: 'forte',           preco: [2.50, 15.00] },
  'gpt-5.5':          { label: 'GPT-5.5',          porte: 'o mais capaz',    preco: [5.00, 30.00] },
};

const LLM_MODELS = {
  anthropic: ['claude-haiku-4-5', 'claude-sonnet-5', 'claude-opus-4-8', 'claude-fable-5'],
  openai: ['gpt-5.4-nano', 'gpt-5.4-mini', 'gpt-5.4', 'gpt-5.5'],
};

// Equivalência entre os dois catálogos, pareada por porte e preço. É o que permite alternar uma
// tarefa de provedor sem descer de patamar sem querer — e, com isso, distribuir o gasto entre as
// duas contas por tarefa, em vez de rateá-lo dentro da mesma peça (o que faria seções da mesma
// petição saírem com vocabulário e cadência diferentes).
const MODEL_EQUIVALENT = {
  // haiku-4-5 (US$ 1,00/5,00) casa com o mini (US$ 0,75/4,50), não com o nano (US$ 0,20/1,25):
  // o nano é outro patamar, e a troca seria uma queda de qualidade que ninguém pediu.
  'claude-haiku-4-5': 'gpt-5.4-mini',
  'claude-sonnet-5': 'gpt-5.4',
  'claude-opus-4-8': 'gpt-5.5',
  'claude-fable-5': 'gpt-5.5',
  'gpt-5.4-nano': 'claude-haiku-4-5',
  'gpt-5.4-mini': 'claude-haiku-4-5',
  'gpt-5.4': 'claude-sonnet-5',
  'gpt-5.5': 'claude-opus-4-8',
};
const ALL_MODELS = [...LLM_MODELS.anthropic, ...LLM_MODELS.openai];
const PROVIDER_LABEL = { anthropic: 'Anthropic', openai: 'OpenAI' };

function modelLabel(id) {
  const m = MODEL_CATALOG[id];
  return m ? `${m.label} · ${m.porte}` : id;
}

function modelPreco(id) {
  const m = MODEL_CATALOG[id];
  if (!m) return null;
  return `US$ ${m.preco[0].toFixed(2)} entrada / US$ ${m.preco[1].toFixed(2)} saída por 1M tokens`;
}

// Taxonomia oficial de 6 tarefas (design.md §Modelos e Preços da change
// cintra-assistente-flutuante-governanca-tokens) — mesmas chaves usadas no backend
// (config_resolver.DEFAULT_TASK_CONFIGS) e no task_type gravado em llm_usage_logs.
const TASKS_KEYS = [
  'chat', 'audit', 'document_import',
  'petition_generation.classificador', 'petition_generation.redator', 'summary',
];
const TASKS_LABELS = {
  chat: 'Chat da Cintra IA',
  audit: 'Auditoria de Ficha',
  document_import: 'Importação de Documento',
  'petition_generation.classificador': 'Petição — Classificação de Pedidos',
  'petition_generation.redator': 'Petição — Redação',
  summary: 'Resumo / Ensinamentos',
};
// O que cada tarefa faz, em uma linha: sem isto a tabela pedia que o escritório escolhesse um
// modelo para uma chave técnica cujo efeito ele não conhecia.
const TASKS_DESCRICAO = {
  chat: 'Responde as dúvidas do time na ficha e aciona cálculo e auditoria.',
  audit: 'Confere a ficha e lista pendências, inconsistências e perguntas ao cliente.',
  document_import: 'Lê a ficha de atendimento em .docx/PDF e preenche os campos.',
  'petition_generation.classificador': 'Decide quais seções e teses a petição terá.',
  'petition_generation.redator': 'Escreve o texto da petição. É a tarefa mais cara e a que mais pesa na qualidade.',
  summary: 'Resume revisões aprovadas e extrai ensinamentos para as próximas peças.',
};
const TASKS_ICON = {
  chat: MessageSquare,
  audit: ShieldCheck,
  document_import: FileCog,
  'petition_generation.classificador': ListChecks,
  'petition_generation.redator': Scale,
  summary: BookOpen,
};
const TASK_DEFAULTS = {
  chat: { provider: 'anthropic', model: 'claude-sonnet-5' },
  audit: { provider: 'anthropic', model: 'claude-sonnet-5' },
  document_import: { provider: 'anthropic', model: 'claude-sonnet-5' },
  'petition_generation.classificador': { provider: 'openai', model: 'gpt-5.4-mini' },
  'petition_generation.redator': { provider: 'anthropic', model: 'claude-opus-4-8' },
  summary: { provider: 'anthropic', model: 'claude-haiku-4-5' },
};

function emptyTaskForm(taskKey) {
  return { ...TASK_DEFAULTS[taskKey], fallback_model: '', max_tokens: '', temperature: '', monthly_budget_brl: '' };
}

function mergeTasksWithDefaults(savedTasks) {
  const tasks = {};
  TASKS_KEYS.forEach(key => {
    const saved = (savedTasks && savedTasks[key]) || {};
    tasks[key] = {
      provider: saved.provider || TASK_DEFAULTS[key].provider,
      model: saved.model || TASK_DEFAULTS[key].model,
      fallback_model: saved.fallback_model || '',
      max_tokens: saved.max_tokens ?? '',
      temperature: saved.temperature ?? '',
      monthly_budget_brl: saved.monthly_budget_brl ?? '',
    };
  });
  return tasks;
}

function cleanTaskPayload(t) {
  const out = { provider: t.provider, model: t.model };
  if (t.fallback_model) out.fallback_model = t.fallback_model;
  if (t.max_tokens !== '' && t.max_tokens != null) out.max_tokens = Number(t.max_tokens);
  if (t.temperature !== '' && t.temperature != null) out.temperature = Number(t.temperature);
  if (t.monthly_budget_brl !== '' && t.monthly_budget_brl != null) out.monthly_budget_brl = String(t.monthly_budget_brl);
  return out;
}

const EMPTY_CREDITS = { anthropic: { loaded_usd: '', loaded_at: '' }, openai: { loaded_usd: '', loaded_at: '' } };

function mergeCredits(saved) {
  const out = { ...EMPTY_CREDITS };
  ['anthropic', 'openai'].forEach(p => {
    const s = (saved && saved[p]) || {};
    out[p] = { loaded_usd: s.loaded_usd || '', loaded_at: s.loaded_at || '' };
  });
  return out;
}

// ─── Um cartão por tarefa ──────────────────────────────────────
function TarefaCard({ taskKey, t, custoMedio, onProvider, onField, onEquivalente }) {
  const Icon = TASKS_ICON[taskKey] || Cpu;
  const modeloDesconhecido = t.model && !MODEL_CATALOG[t.model];
  const preco = modelPreco(t.model);
  const equivalente = MODEL_EQUIVALENT[t.model];

  return (
    <div className="ia-task-card">
      <div className="ia-task-head">
        <div className="ia-task-icon"><Icon size={15} /></div>
        <div className="ia-task-head-text">
          <div className="ia-task-name">{TASKS_LABELS[taskKey]}</div>
          <div className="ia-task-what">{TASKS_DESCRICAO[taskKey]}</div>
        </div>
      </div>

      <div className="ia-field">
        <label id={`prov-${taskKey}`}>Provedor</label>
        <div className="ia-seg" role="group" aria-labelledby={`prov-${taskKey}`}>
          {['anthropic', 'openai'].map(p => (
            <button
              key={p}
              type="button"
              aria-pressed={t.provider === p}
              onClick={() => onProvider(taskKey, p)}
            >
              {PROVIDER_LABEL[p]}
            </button>
          ))}
        </div>
      </div>

      <div className="ia-field">
        <label htmlFor={`model-${taskKey}`}>Modelo</label>
        <select
          id={`model-${taskKey}`}
          value={t.model}
          onChange={e => onField(taskKey, 'model', e.target.value)}
        >
          {/* Modelo salvo que não está no catálogo continua selecionável: o campo já foi texto
              livre, e sumir com a escolha do escritório seria trocá-la sem avisar. */}
          {modeloDesconhecido && <option value={t.model}>{t.model} (fora do catálogo)</option>}
          {LLM_MODELS[t.provider].map(m => <option key={m} value={m}>{modelLabel(m)}</option>)}
        </select>
        {preco && <span className="ia-hint">{preco}</span>}
      </div>

      <div className="ia-field">
        <label htmlFor={`fb-${taskKey}`}>Fallback</label>
        <select
          id={`fb-${taskKey}`}
          value={t.fallback_model}
          onChange={e => onField(taskKey, 'fallback_model', e.target.value)}
        >
          <option value="">Automático</option>
          {ALL_MODELS.map(m => <option key={m} value={m}>{modelLabel(m)}</option>)}
        </select>
        <span className="ia-hint">
          {t.fallback_model
            ? 'Usado quando a chave do provedor acima não está configurada.'
            : 'Sem chave do provedor acima, cai no equivalente do outro provedor.'}
        </span>
      </div>

      <details className="ia-advanced">
        <summary>Ajustes finos</summary>
        <div className="ia-advanced-grid">
          <div className="ia-field">
            <label htmlFor={`mt-${taskKey}`}>Max tokens</label>
            <input id={`mt-${taskKey}`} type="number" min="1" value={t.max_tokens} placeholder="Padrão"
              onChange={e => onField(taskKey, 'max_tokens', e.target.value)} />
          </div>
          <div className="ia-field">
            <label htmlFor={`tp-${taskKey}`}>Temperatura</label>
            <input id={`tp-${taskKey}`} type="number" min="0" max="1" step="0.1" value={t.temperature} placeholder="Padrão"
              onChange={e => onField(taskKey, 'temperature', e.target.value)} />
          </div>
          <div className="ia-field">
            <label htmlFor={`ob-${taskKey}`}>Orçamento/mês</label>
            <input id={`ob-${taskKey}`} type="number" min="0" step="0.01" value={t.monthly_budget_brl} placeholder="Sem limite"
              onChange={e => onField(taskKey, 'monthly_budget_brl', e.target.value)} />
          </div>
        </div>
      </details>

      <div className="ia-task-meta">
        <span>Custo médio por chamada</span>
        <span className="ia-task-price">{custoMedio}</span>
        {equivalente && (
          <button
            type="button"
            className="btn btn-ghost btn-sm ia-equivalente"
            onClick={() => onEquivalente(taskKey, equivalente)}
            title={`Troca esta tarefa para ${modelLabel(equivalente)} no outro provedor, sem mudar de patamar`}
          >
            <Repeat size={12} /> Usar {MODEL_CATALOG[equivalente]?.label || equivalente}
          </button>
        )}
      </div>
    </div>
  );
}

function LlmProviderCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null); // 'anthropic' | 'openai' | null
  const [masked, setMasked] = useState({
    anthropic_api_key_masked: '', openai_api_key_masked: '',
    anthropic_admin_key_masked: '', openai_admin_key_masked: '',
  });
  const [form, setForm] = useState({
    provider: 'anthropic',
    anthropic_api_key: '',
    openai_api_key: '',
    anthropic_admin_key: '',
    openai_admin_key: '',
    credits: mergeCredits(null),
    tasks: mergeTasksWithDefaults(null),
  });
  const [usageData, setUsageData] = useState(null);

  useEffect(() => {
    settingsApi.getLlm()
      .then(data => {
        setForm(f => ({
          ...f,
          provider: data.provider || 'anthropic',
          tasks: mergeTasksWithDefaults(data.tasks),
          credits: mergeCredits(data.credits),
        }));
        setMasked({
          anthropic_api_key_masked: data.anthropic_api_key_masked || '',
          openai_api_key_masked: data.openai_api_key_masked || '',
          anthropic_admin_key_masked: data.anthropic_admin_key_masked || '',
          openai_admin_key_masked: data.openai_admin_key_masked || '',
        });
      })
      .catch(() => toast.error('Não foi possível carregar a configuração de IA'))
      .finally(() => setLoading(false));

    metricsApi.getLlmUsage()
      .then(data => setUsageData(data))
      .catch(() => {});
  }, []);

  function setField(field, value) {
    setForm(f => ({ ...f, [field]: value }));
  }

  function setCredit(provedor, campo, valor) {
    setForm(f => ({ ...f, credits: { ...f.credits, [provedor]: { ...f.credits[provedor], [campo]: valor } } }));
  }

  function setTaskField(taskKey, field, value) {
    setForm(f => ({
      ...f,
      tasks: { ...f.tasks, [taskKey]: { ...f.tasks[taskKey], [field]: value } },
    }));
  }

  // Troca provedor e modelo de uma vez pelo par equivalente. Mexer no provedor sozinho jogaria a
  // tarefa no primeiro modelo do outro catálogo, que pode ser um patamar acima ou abaixo.
  function trocarPeloEquivalente(taskKey, modeloAlvo) {
    const provedorAlvo = LLM_MODELS.anthropic.includes(modeloAlvo) ? 'anthropic' : 'openai';
    setForm(f => ({
      ...f,
      tasks: { ...f.tasks, [taskKey]: { ...f.tasks[taskKey], provider: provedorAlvo, model: modeloAlvo } },
    }));
  }

  function handleTaskProviderChange(taskKey, newProvider) {
    setForm(f => {
      const atual = f.tasks[taskKey];
      if (atual.provider === newProvider) return f;
      return {
        ...f,
        tasks: {
          ...f.tasks,
          // Trocar de provedor invalida o modelo escolhido (são catálogos distintos): cai no
          // primeiro do provedor novo, como antes.
          [taskKey]: { ...atual, provider: newProvider, model: LLM_MODELS[newProvider][0] },
        },
      };
    });
  }

  async function handleSave() {
    setSaving(true);
    try {
      const payload = {
        provider: form.provider,
        anthropic_api_key: form.anthropic_api_key || undefined,
        openai_api_key: form.openai_api_key || undefined,
        // Campo em branco mantém a chave salva — é assim que ele pode ficar mascarado na tela
        // sem exigir redigitação a cada save. Para apagar existe o botão "Remover".
        anthropic_admin_key: form.anthropic_admin_key || undefined,
        openai_admin_key: form.openai_admin_key || undefined,
        credits: form.credits,
        tasks: Object.fromEntries(TASKS_KEYS.map(k => [k, cleanTaskPayload(form.tasks[k])])),
      };
      const updated = await settingsApi.updateLlm(payload);
      setMasked({
        anthropic_api_key_masked: updated.anthropic_api_key_masked || '',
        openai_api_key_masked: updated.openai_api_key_masked || '',
        anthropic_admin_key_masked: updated.anthropic_admin_key_masked || '',
        openai_admin_key_masked: updated.openai_admin_key_masked || '',
      });
      setForm(f => ({
        ...f,
        anthropic_api_key: '',
        openai_api_key: '',
        anthropic_admin_key: '',
        openai_admin_key: '',
        tasks: mergeTasksWithDefaults(updated.tasks),
        credits: mergeCredits(updated.credits),
      }));
      toast.success('Configuração de IA salva.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao salvar configuração de IA');
    } finally {
      setSaving(false);
    }
  }

  async function handleTest(provName) {
    setTesting(provName);
    try {
      const apiKeyField = provName === 'anthropic' ? 'anthropic_api_key' : 'openai_api_key';
      const result = await settingsApi.testLlm({
        provider: provName,
        api_key: form[apiKeyField] || undefined
      });
      if (result.ok) toast.success(`${PROVIDER_LABEL[provName]}: ${result.message || 'Conexão bem-sucedida!'}`);
      else toast.error(`${PROVIDER_LABEL[provName]}: ${result.message || 'Falha na conexão'}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao testar conexão');
    } finally {
      setTesting(null);
    }
  }

  async function removerChaveAdmin(provedor) {
    // String vazia é o que apaga no backend; o campo em branco do formulário significa "manter",
    // então apagar precisa ser um pedido explícito.
    try {
      const updated = await settingsApi.updateLlm({
        provider: form.provider,
        [`${provedor}_admin_key`]: '',
      });
      setMasked(m => ({ ...m, [`${provedor}_admin_key_masked`]: updated[`${provedor}_admin_key_masked`] || '' }));
      setForm(f => ({ ...f, [`${provedor}_admin_key`]: '' }));
      toast.success(`Chave de administração da ${PROVIDER_LABEL[provedor]} removida.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao remover a chave de administração');
    }
  }

  function getModelAverageCost(modelName) {
    if (!usageData || !usageData.by_model) return '—';
    const bucket = usageData.by_model.find(b => b.key === modelName);
    if (!bucket || !bucket.count) return 'R$ 0,00';
    const avg = bucket.custo_total / bucket.count;
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(avg);
  }

  const isConnected = masked.anthropic_api_key_masked || masked.openai_api_key_masked;

  function ChaveCard({ provedor, placeholder, adminPlaceholder, painel }) {
    const chaveField = `${provedor}_api_key`;
    const adminField = `${provedor}_admin_key`;
    const chaveMasked = masked[`${provedor}_api_key_masked`];
    const adminMasked = masked[`${provedor}_admin_key_masked`];
    return (
      <div className="ia-key-card">
        <div className="ia-key-title">{PROVIDER_LABEL[provedor]}</div>

        <FG label="Chave de uso" hint={chaveMasked ? `Atual: ${chaveMasked}` : 'Não configurada'}>
          <input type="password" autoComplete="off" value={form[chaveField]}
            onChange={e => setField(chaveField, e.target.value)}
            placeholder={chaveMasked || placeholder} />
        </FG>
        <button className="btn btn-ghost btn-sm" onClick={() => handleTest(provedor)} disabled={testing !== null}>
          <Wifi size={12} /> {testing === provedor ? 'Testando...' : 'Testar conexão'}
        </button>

        <FG
          label="Chave de administração (opcional)"
          hint={adminMasked
            ? `Atual: ${adminMasked} — deixe em branco para manter`
            : `Só para ler o gasto real em ${painel}. É diferente da chave de uso.`}
        >
          <input type="password" autoComplete="off" value={form[adminField]}
            onChange={e => setField(adminField, e.target.value)}
            placeholder={adminMasked || adminPlaceholder} />
        </FG>
        {adminMasked && (
          <button className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }}
            onClick={() => removerChaveAdmin(provedor)}>
            <Trash2 size={12} /> Remover chave de administração
          </button>
        )}

        <div className="form-grid fg-2" style={{ gap: '.5rem' }}>
          <FG label="Crédito carregado (US$)">
            <input type="number" min="0" step="0.01" placeholder="10.00"
              value={form.credits[provedor].loaded_usd}
              onChange={e => setCredit(provedor, 'loaded_usd', e.target.value)} />
          </FG>
          <FG label="Data da carga">
            <input type="date" value={form.credits[provedor].loaded_at}
              onChange={e => setCredit(provedor, 'loaded_at', e.target.value)} />
          </FG>
        </div>
      </div>
    );
  }

  return (
    <IntegrationCard icon={Cpu} title="Chaves, modelos e crédito"
      description="Quais modelos executam cada tarefa do sistema, e quanto ainda há de crédito em cada provedor."
      status={loading ? 'pending' : (isConnected ? 'connected' : 'disconnected')}>
      {loading ? (
        <div style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>Carregando…</div>
      ) : (
        <>
          <div className="ia-key-grid" style={{ marginBottom: '1.25rem' }}>
            <ChaveCard provedor="anthropic" placeholder="sk-ant-..." adminPlaceholder="sk-ant-admin01-..."
              painel="console.anthropic.com" />
            <ChaveCard provedor="openai" placeholder="sk-proj-..." adminPlaceholder="sk-admin-..."
              painel="platform.openai.com" />
          </div>

          <div className="alert alert-info" style={{ marginBottom: '1.25rem', fontSize: '.76rem', alignItems: 'flex-start' }}>
            <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
            <span>
              Nem a Anthropic nem a OpenAI publicam o saldo restante numa API estável. O saldo do
              dashboard é <strong>o que você carregou menos o que foi gasto</strong> — por isso os dois
              campos acima. Com a chave de administração o gasto vem medido pelo provedor; sem ela,
              é estimado pelo consumo registrado neste sistema.
            </span>
          </div>

          <SepConfigLabel>Qual modelo executa cada tarefa</SepConfigLabel>
          <div className="ia-task-grid" style={{ marginBottom: '1.25rem' }}>
            {TASKS_KEYS.map(taskKey => (
              <TarefaCard
                key={taskKey}
                taskKey={taskKey}
                t={form.tasks[taskKey] || emptyTaskForm(taskKey)}
                custoMedio={getModelAverageCost((form.tasks[taskKey] || emptyTaskForm(taskKey)).model)}
                onProvider={handleTaskProviderChange}
                onField={setTaskField}
                onEquivalente={trocarPeloEquivalente}
              />
            ))}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-gold btn-sm" onClick={handleSave} disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar configurações'}
            </button>
          </div>
        </>
      )}
    </IntegrationCard>
  );
}

// ─── CONSUMO DE LLM ────────────────────────────────────────────
const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const INT = new Intl.NumberFormat('pt-BR');

function SectionConsumoLLM() {
  const [filters, setFilters] = useState({ date_from: '', date_to: '', task_type: '', provider: '' });
  const [usage, setUsage] = useState(null);
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(true);

  function load(activeFilters) {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(activeFilters).filter(([, v]) => v));
    Promise.all([
      metricsApi.getLlmUsage(params),
      metricsApi.getLlmUsageBudgetStatus(),
    ])
      .then(([usageData, budgetData]) => { setUsage(usageData); setBudget(budgetData); })
      .catch(() => toast.error('Não foi possível carregar o consumo de IA'))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    load(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function setFilter(field, value) {
    setFilters(f => ({ ...f, [field]: value }));
  }

  const budgetByTask = Object.fromEntries((budget?.tasks || []).map(t => [t.task_type, t]));

  return (
    <div style={{ marginTop: '1.25rem' }}>
      <IntegrationCard icon={BarChart3} title="Consumo de Tokens e Custos"
        description="Visão consolidada de toda chamada de LLM do sistema, por tarefa, provedor, modelo, caso e usuário."
        status={loading ? 'pending' : 'connected'}>
        {/* Filtros */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.6rem', alignItems: 'flex-end', marginBottom: '1rem' }}>
          <FG label="De" style={{ minWidth: 130 }}>
            <input type="date" value={filters.date_from} onChange={e => setFilter('date_from', e.target.value)} />
          </FG>
          <FG label="Até" style={{ minWidth: 130 }}>
            <input type="date" value={filters.date_to} onChange={e => setFilter('date_to', e.target.value)} />
          </FG>
          <FG label="Tarefa" style={{ minWidth: 170 }}>
            <select value={filters.task_type} onChange={e => setFilter('task_type', e.target.value)}>
              <option value="">Todas</option>
              {TASKS_KEYS.map(k => <option key={k} value={k}>{TASKS_LABELS[k]}</option>)}
            </select>
          </FG>
          <FG label="Provedor" style={{ minWidth: 130 }}>
            <select value={filters.provider} onChange={e => setFilter('provider', e.target.value)}>
              <option value="">Todos</option>
              <option value="anthropic">Anthropic</option>
              <option value="openai">OpenAI</option>
            </select>
          </FG>
          <button className="btn btn-outline btn-sm" onClick={() => load(filters)} disabled={loading}>
            Filtrar
          </button>
        </div>

        {loading ? (
          <div style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>Carregando…</div>
        ) : !usage ? (
          <div style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>Sem dados de consumo ainda.</div>
        ) : (
          <>
            {/* Totais */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '.75rem', marginBottom: '1.25rem' }}>
              {[
                ['Chamadas', INT.format(usage.total_calls)],
                ['Tokens de entrada', INT.format(usage.tokens_in)],
                ['Tokens de saída', INT.format(usage.tokens_out)],
                ['Custo total', BRL.format(usage.custo_total)],
              ].map(([label, value]) => (
                <div key={label} style={{ padding: '.6rem .75rem', border: '1px solid var(--border-light)', borderRadius: 'var(--radius-md)', background: 'var(--bg)' }}>
                  <div style={{ fontSize: '.68rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.03em' }}>{label}</div>
                  <div style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>{value}</div>
                </div>
              ))}
            </div>

            {/* Por tarefa, com orçamento */}
            <div style={{ marginBottom: '1.25rem' }}>
              <Table head={['Tarefa', 'Chamadas', 'Tokens in/out', 'Custo', 'Orçamento do mês']}>
                {usage.by_task_type.map(b => {
                  const budgetInfo = budgetByTask[b.key];
                  return (
                    <TR key={b.key}>
                      <TD>{TASKS_LABELS[b.key] || b.key}</TD>
                      <TD>{INT.format(b.count)}</TD>
                      <TD>{INT.format(b.tokens_in)} / {INT.format(b.tokens_out)}</TD>
                      <TD>{BRL.format(b.custo_total)}</TD>
                      <TD>
                        {!budgetInfo || budgetInfo.orcamento_mensal_brl == null ? (
                          <span style={{ color: 'var(--text-muted)' }}>Sem orçamento</span>
                        ) : (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: '.3rem',
                            color: budgetInfo.estourado ? 'var(--error)' : budgetInfo.alerta ? 'var(--warning)' : 'var(--text-secondary)',
                            fontWeight: budgetInfo.alerta ? 700 : 400,
                          }}>
                            {budgetInfo.alerta && <AlertTriangle size={12} />}
                            {BRL.format(budgetInfo.custo_mes_atual_brl)} / {BRL.format(budgetInfo.orcamento_mensal_brl)}
                            {' '}({Math.round(budgetInfo.percentual)}%)
                          </span>
                        )}
                      </TD>
                    </TR>
                  );
                })}
              </Table>
            </div>

            {/* Por provider / modelo */}
            <div className="form-grid fg-2" style={{ gap: '1rem', marginBottom: '1.25rem' }}>
              <div>
                <div style={{ fontSize: '.75rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '.4rem' }}>Por provedor</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '.3rem' }}>
                  {usage.by_provider.map(b => (
                    <div key={b.key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.75rem', padding: '.3rem .5rem', background: 'var(--bg)', borderRadius: '4px' }}>
                      <span>{b.key}</span>
                      <span style={{ fontFamily: 'var(--font-mono)' }}>{BRL.format(b.custo_total)}</span>
                    </div>
                  ))}
                  {usage.by_provider.length === 0 && <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>—</div>}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '.75rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '.4rem' }}>Por modelo</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '.3rem' }}>
                  {usage.by_model.map(b => (
                    <div key={b.key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.75rem', padding: '.3rem .5rem', background: 'var(--bg)', borderRadius: '4px' }}>
                      <span style={{ fontFamily: 'var(--font-mono)' }}>{b.key}</span>
                      <span style={{ fontFamily: 'var(--font-mono)' }}>{BRL.format(b.custo_total)}</span>
                    </div>
                  ))}
                  {usage.by_model.length === 0 && <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>—</div>}
                </div>
              </div>
            </div>

            {/* Top casos / usuários */}
            <div className="form-grid fg-2" style={{ gap: '1rem' }}>
              <div>
                <div style={{ fontSize: '.75rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '.4rem' }}>Top casos por custo</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '.3rem' }}>
                  {usage.top_cases.map(item => (
                    <div key={item.key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.72rem', padding: '.3rem .5rem', background: 'var(--bg)', borderRadius: '4px' }}>
                      <span style={{ fontFamily: 'var(--font-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.key}</span>
                      <span style={{ fontFamily: 'var(--font-mono)', flexShrink: 0, marginLeft: '.5rem' }}>{BRL.format(item.custo_total)}</span>
                    </div>
                  ))}
                  {usage.top_cases.length === 0 && <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>Sem dados no período.</div>}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '.75rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '.4rem' }}>Top usuários por custo</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '.3rem' }}>
                  {usage.top_users.map(item => (
                    <div key={item.key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.72rem', padding: '.3rem .5rem', background: 'var(--bg)', borderRadius: '4px' }}>
                      <span style={{ fontFamily: 'var(--font-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.key}</span>
                      <span style={{ fontFamily: 'var(--font-mono)', flexShrink: 0, marginLeft: '.5rem' }}>{BRL.format(item.custo_total)}</span>
                    </div>
                  ))}
                  {usage.top_users.length === 0 && <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>Sem dados no período.</div>}
                </div>
              </div>
            </div>
          </>
        )}
      </IntegrationCard>
    </div>
  );
}

function SectionIA() {
  return (
    <div>
      <SectionHeader
        title="Inteligência Artificial"
        description="Provedor, modelos e orçamento por tarefa da Cintra IA — chat, auditoria, importação, geração de petição e resumo"
      />
      <LlmProviderCard />
      <SectionConsumoLLM />
    </div>
  );
}

const SECTION_COMPONENTS = {
  usuarios:        SectionUsuarios,
  perfis:          SectionPerfis,
  escritorio:      SectionEscritorio,
  jurisprudencias: SectionJurisprudencias,
  textos_padrao:   TextosPadrao,
  parametros:      SectionParametros,
  campos:          SectionCampos,
  templates:       SectionTemplates,
  integracoes:     SectionIntegracoes,
  ia:              SectionIA,
};

export default function Configuracoes() {
  const { user } = useAuthStore();
  const [active, setActive] = useState('usuarios');
  const permissions = user?.permissions || [];
  const visibleSections = SECTIONS.filter(s =>
    (SECTION_PERMISSIONS[s.id] || []).some(permission => permissions.includes(permission))
  );

  useEffect(() => {
    if (visibleSections.length && !visibleSections.some(s => s.id === active)) {
      setActive(visibleSections[0].id);
    }
  }, [active, visibleSections]);

  const activeSection = visibleSections.some(s => s.id === active) ? active : visibleSections[0]?.id;
  const ActiveSection = SECTION_COMPONENTS[activeSection];

  return (
    <div className="settings-layout">

      {/* ── SIDEBAR ── */}
      <div className="settings-sidebar">
        <div className="card" style={{ padding: '.5rem 0' }}>
          <div style={{ padding: '.625rem 1rem .375rem', fontSize: '.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>
            Configurações
          </div>
          {visibleSections.map(s => {
            const Icon = s.icon;
            const isActive = activeSection === s.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setActive(s.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '.625rem',
                  width: '100%', padding: '.55rem 1rem', border: 'none', cursor: 'pointer',
                  background: isActive ? 'rgba(200,144,42,.13)' : 'transparent',
                  color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
                  fontWeight: isActive ? 600 : 400, fontSize: '.82rem', textAlign: 'left',
                  transition: 'background .12s, color .12s',
                }}
              >
                <Icon size={14} />
                {s.label}
                {isActive && <ChevronRight size={12} style={{ marginLeft: 'auto' }} />}
              </button>
            );
          })}

          <div style={{ margin: '.75rem 1rem 0', padding: '.625rem 0', borderTop: '1px solid var(--border-light)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
              <UserAvatar user={user} size={28} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '.75rem', fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.name}</div>
                <div style={{ fontSize: '.68rem', color: 'var(--text-muted)' }}>{user?.role_label}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── CONTEÚDO ── */}
      <div>
        {ActiveSection
          ? <ActiveSection />
          : <div className="card" style={{ padding: '1.5rem', color: 'var(--text-muted)', fontSize: '.82rem' }}>Nenhuma seção de configuração liberada para este perfil.</div>}
      </div>
    </div>
  );
}
